// Operations chats sidebar (9 Oct 2026) — Reputifly Build's 8 Oct remodel with
// Ops semantics (julian-ui look). Clients | Samples switch, then:
//   Attention (pending revisions; archived clients too) → Pinned (the team's
//   daily follow-ups) → Unread → day groups sorted by last activity
//   (automated activity — revisions, Exchange, new samples, deploys — bumps a chat).
// Each chat is in exactly one place per view (a pinned chat is never repeated under Today).
// Pin / archive are shared by everyone and never change the chat itself.
// Phone: swipe a row left for Pin · Archive · More (a deep swipe archives),
// hold a row in the list to pin it (a pinned, Attention or archived row opens its sheet instead).
// Laptop: ⋯ and a pin button on hover, right-click menu.
// Pinned (9 Oct redesign, no limit — Julian pins ~17+ a day for Farhan's follow-ups) NEVER has
// its own scroll box and never cuts a pin in half:
//   - laptop / touch panel: compact one-line rows (name + one state trail), on the same face and name
//     columns as every other row. Up to a budget every pin shows; past it, the first pins in pin order
//     and one "Show N more" line that carries the hidden pins' states (needs you · working · unread).
//     The budget (3–8 lines) is what fits under two Attention rows while the next section keeps its
//     label and one full row in view, from the list's measured room (fixer r1 #1); it never follows
//     the live Attention count, so Pinned holds still when a revision lands (break-it r2);
//   - phone list: a wrapping grid of face tiles, 2 rows (1 when two would push the list off), the
//     last cell a folder "8 more" that names what is hidden.
//   Showing everything is remembered per device; the sticky head keeps "Show less" in reach.
// The header's Filter button (All · Unread · Working · Pinned, live counts, remembered per device;
// the active choice again = All chats) lists every unread, working or pinned chat as full rows.
// Mark as unread: the row / card menu, or swipe a row right (WhatsApp).
// Working: the avatar hops, a pulsing dot sits on it and the thinking orb leads the words,
// which cycle "Working… Thinking… Reading… Writing…" (SwapText, ~2.5 s; static with reduced motion).
// Ordering, grouping, folding, placement and words live in lib/sidebarModel.ts (pure, tested).
// Every style is scoped under .sb* in styles/sidebar.css.
import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useLiveAttention } from "../lib/useLiveAttention";
import type { Company, SessionStatus } from "../lib/types";
import { useSession } from "../store/session";
import ChatAvatar from "./ChatAvatar";
import SwapText from "./SwapText";
import {
  FILTER_LABEL, FILTER_ORDER, KIND_LABEL, isWorking, WORKING_VERBS, WORKING_VERB_MS, archivedCount as countArchived, attentionChats, chatKind, deleteCopy,
  filterCounts, foldPinned, formatExactTime, formatRowTime, isArchived, isPinned, isSidebarFilter, lastActivityMs, listSections,
  matchRanges, nameConfirmed, pinBudget, pinnedChats, placePopover, previewText, quietLine, rowState, shortName, siteHost, visibleChats,
  SIDEBAR_WIDTH, clampSidebarWidth, readSidebarWidth, writeSidebarWidth,
  type ChatActivity, type PinRoom, type PopoverPlacement, type RowState, type SidebarFilter, type SidebarInputs, type SidebarTab,
} from "../lib/sidebarModel";
import "../styles/helpers.css";
import "../styles/sidebar.css";
import "../styles/sidebar-shell.css";

const ThinkingOrb = lazy(() => import("thinking-orbs").then((m) => ({ default: m.ThinkingOrb })));

const ACTIVITY_POLL_MS = 30_000;
/** Hold a row this long to pin it / open its sheet (touch). Moving more than PRESS_SLOP cancels. */
const LONG_PRESS_MS = 500;
const PRESS_SLOP = 8;
const UNDO_MS = 6000;
const SWIPE_ACTION_W = 76;
const SWIPE_SLOP = 10;
const SWIPE_SETTLE_MS = 340;
const SWIPE_FLICK = 0.35;
const SWIPE_DEEP_SHARE = 0.62;
const DEEP_OUT_MS = 200, DEEP_COLLAPSE_MS = 220;
const DEEP_DEDUPE_MS = 2000;
/** Swipe a row right this far and let go: Mark as unread (or read). */
const UNREAD_ARM_PX = 72;
const FILTER_KEY = "ops.sidebar.filter.v1";
// v2 (9 Oct redesign): a fold set to get away from the old scroll box starts open again.
const PINNED_FOLD_KEY = "ops.sidebar.pinnedFolded.v2";
/** "Show all pinned" (past the height budget), remembered per device for rows and tiles alike. */
const PINNED_EXPANDED_KEY = "ops.sidebar.pinnedExpanded.v1";
/** Rows (laptop / touch panel), the Show line and the phone grid's tiles walk together with ↑ / ↓
 *  (search ↓ lands on the first). Inside the grid ↑ / ↓ move by a row of tiles; past its first or
 *  last row they carry on to the row above / below (fixer r1 #3). Only the shown ones count. */
const ROVING = ".sbRow[tabindex='0'], .sbPinMore, .sbTile";
function storage(): Storage | null { try { return window.localStorage; } catch { return null; } }
function readPref(key: string): string | null { try { return window.localStorage.getItem(key); } catch { return null; } }
function writePref(key: string, value: string) { try { window.localStorage.setItem(key, value); } catch { /* private mode */ } }

interface Props {
  open: boolean;
  companies: Company[];
  activeCompanyId: string | null;
  onSelect: (id: string) => void;
  onSettings: (id: string) => void;
  onDelete: (id: string) => void;
  onConvertToClient: (id: string) => void;
  onClearHistory: (id: string) => void;
  onCopyCurl: (id: string) => void;
  onVisitWebsite: (id: string) => void;
  pendingByCompany: Record<string, boolean>;
  sessionStatusByCompany: Record<string, SessionStatus>;
  onRename: (id: string, newName: string) => void;
  onAddCompany: () => void;
  onAddSample: () => void;
  onCloseSidebar: () => void;
}

/** An open menu. Every open is a new object, so it is also the key of that open's measurement. */
type Menu = { id: string; x?: number; y?: number; anchor?: DOMRect | null; place?: string };
/** The menu id of the header's Filter popover (never a chat id: those are [a-z0-9_-]). */
const FILTER_MENU = " filter";
type Undo = { kind: "archive" | "pin" | "unpin"; id: string; name: string; pinned: boolean; pinnedAt?: string | null };

function rectOf(el: unknown): DOMRect | null {
  const node = el as { getBoundingClientRect?: () => DOMRect } | null;
  return node && typeof node.getBoundingClientRect === "function" ? node.getBoundingClientRect() : null;
}
/** Laptop with a mouse: menus anchor to the button; touch / narrow: bottom sheet. */
function popoverMode(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia("(min-width: 760px) and (hover: hover)").matches;
}
/** Pinned rows (laptop) and tiles (phone) are both in the page; CSS shows one. Only shown ones take focus. */
function visibleOnly(list: HTMLElement[]): HTMLElement[] { return list.filter((el) => el.getClientRects().length > 0); }
function isTextField(el: Element | null): boolean {
  return !!el && (el.matches?.("input:not([type=button]):not([type=checkbox]):not([type=radio]), textarea, select") || (el as HTMLElement).isContentEditable === true);
}
function reducedMotion(): boolean {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}

/* One shared clock for the working words, so every working row changes word together (calm),
   ticking only while some row shows it and the tab is visible; never with reduced motion. */
const verbClock = { n: 0, timer: 0, subs: new Set<() => void>() };
function subscribeVerb(cb: () => void) {
  verbClock.subs.add(cb);
  if (!verbClock.timer && !reducedMotion()) {
    verbClock.timer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      verbClock.n++;
      verbClock.subs.forEach((fn) => fn());
    }, WORKING_VERB_MS);
  }
  return () => {
    verbClock.subs.delete(cb);
    if (!verbClock.subs.size && verbClock.timer) { window.clearInterval(verbClock.timer); verbClock.timer = 0; }
  };
}
/** "Working… → Thinking… → Reading… → Writing…" in place (SwapText's slide/blur + shimmer). */
function WorkingWord({ className = "" }: { className?: string }) {
  const n = useSyncExternalStore(subscribeVerb, () => verbClock.n, () => 0);
  return <SwapText text={WORKING_VERBS[n % WORKING_VERBS.length]} shimmer className={`sbVerb ${className}`} />;
}

/** A phone face tile's height (padding 8 + face 52 + gap 6 + two name lines 30 + padding 6; sidebar.css). */
const TILE_H = 102;
type Room = Omit<PinRoom, "attentionRows"> & { vh: number; vw: number };
/** The list's real room (fixer r1 #1): from the list's top to the footer, so the Undo bar between
 *  them never folds a pin, plus the row / pinned row / head / label sizes the CSS uses right now
 *  (custom properties on .sb, set per pointer and phone size in sidebar.css). */
function measureRoom(list: HTMLElement | null): Room | null {
  const sb = list?.closest<HTMLElement>(".sb");
  const footer = sb?.querySelector<HTMLElement>(".sb__footer");
  if (!list || !sb || !footer) return null;
  const height = footer.getBoundingClientRect().top - list.getBoundingClientRect().top;
  if (!(height > 0)) return null;
  const cs = getComputedStyle(sb);
  const px = (name: string, fallback: number) => parseFloat(cs.getPropertyValue(name)) || fallback;
  return {
    vh: window.innerHeight, vw: window.innerWidth, list: height,
    rowH: px("--sb-row-h", 64), pinH: px("--sb-pin-h", 36), headH: px("--sb-pin-head-h", 34), labelH: px("--sb-label-h", 34), tileH: TILE_H,
  };
}
/** Attention's room in the fold budget is a constant (break-it r2): the fold used to follow the live
 *  row count, so Pinned refolded on every load once Attention arrived and again whenever a revision
 *  landed or was approved. Two rows is what Attention holds on a normal day, so the r1 promise (the
 *  next section keeps its label and one full row in view) holds with up to two revisions waiting. */
const ATTENTION_RESERVE_ROWS = 2;
/** The fold budget follows the room the list really has (never the Undo bar), less a fixed room for
 *  Attention. It is measured on first paint, on resize and when the Archived view closes — never on a
 *  status poll or an Attention change, so who shows holds still. It never changes while a text field
 *  has focus (a phone keyboard resizing the page). */
function usePinBudget(listRef: React.RefObject<HTMLDivElement | null>, showArchived: boolean) {
  const [room, setRoom] = useState<Room | null>(null);
  const measure = () => {
    const next = measureRoom(listRef.current);
    if (!next) return;
    setRoom((was) => (was && Object.keys(next).every((k) => Math.abs((was as Record<string, number>)[k] - (next as Record<string, number>)[k]) < 0.5) ? was : next));
  };
  useLayoutEffect(() => { if (!showArchived) measure(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showArchived]);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (isTextField(document.activeElement)) return;
        measure();
      });
    };
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    document.addEventListener("focusout", update);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      document.removeEventListener("focusout", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const vh = room?.vh ?? (typeof window === "undefined" ? 800 : window.innerHeight);
  const vw = room?.vw ?? (typeof window === "undefined" ? 1280 : window.innerWidth);
  return useMemo(() => pinBudget(vh, vw, room && { ...room, attentionRows: ATTENTION_RESERVE_ROWS }), [vh, vw, room]);
}
function vibrate(ms: number) {
  try { (navigator as Navigator & { vibrate?: (ms: number) => boolean }).vibrate?.(ms); } catch { /* no haptics */ }
}

function highlight(text: string, query: string): React.ReactNode {
  if (!query.trim() || !text) return text;
  const ranges = matchRanges(text, query);
  if (!ranges.length) return text;
  const out: React.ReactNode[] = [];
  let at = 0;
  ranges.forEach(([start, end], i) => {
    if (start > at) out.push(text.slice(at, start));
    out.push(<mark key={i} className="sbHit">{text.slice(start, end)}</mark>);
    at = end;
  });
  if (at < text.length) out.push(text.slice(at));
  return out;
}

/** The thinking orb. The library only draws sizes 20 / 32 / 64 (anything else throws); 20 always. */
function WorkingOrb() {
  return (
    <span className="sbOrb" aria-hidden="true">
      <Suspense fallback={<span className="sbOrb__dot" />}>
        <ThinkingOrb state="solving" size={20} theme="dark" />
      </Suspense>
    </span>
  );
}

/** The chat's face: the bot hops while its agent works, with a pulsing dot on it (static when motion is reduced). */
function Face({ id, working, size }: { id: string; working: boolean; size?: number | string }) {
  return (
    <span className={`sbFace ${working ? "is-working" : ""}`}>
      <ChatAvatar id={id} working={working} size={size} />
      {working && <span className="sbFace__pulse" aria-hidden="true" />}
    </span>
  );
}

/* Lucide-style icons (stroke 1.75, currentColor). */
const I = {
  pin: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><path d="M12 17v5" /><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" /></svg>,
  unpin: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><path d="M12 17v5" /><path d="M15 9.34V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H7.89" /><path d="m2 2 20 20" /><path d="M9 9v1.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h11" /></svg>,
  archive: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><rect x="2" y="3" width="20" height="5" rx="1" /><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" /><path d="M10 12h4" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>,
  convert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><polyline points="16 11 18 13 22 9" /></svg>,
  settings: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></svg>,
  site: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>,
  copy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>,
  compact: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" /></svg>,
  clear: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
  more: <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" width="20" height="20" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>,
  filter: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="19" height="19" aria-hidden="true"><path d="M3 6h18" /><path d="M7 12h10" /><path d="M10 18h4" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>,
  unread: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><path d="M22 10.5V12a10 10 0 1 1-6.22-9.26" /><circle cx="19" cy="5" r="3" fill="currentColor" stroke="none" /></svg>,
  read: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="16 9.5 10.5 15 8 12.5" /></svg>,
  chevron: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>,
  chevronUp: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true"><polyline points="18 15 12 9 6 15" /></svg>,
  /** A small pin: the mark on a pinned chat shown outside Pinned (Attention, search, the Unread filter). */
  pinMark: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden="true"><path d="M12 17v5" /><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" /></svg>,
  /** The blue dot Mark as unread puts on a row (colour = meaning), drawn as an icon. */
  dot: <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="sbMenu__dot"><circle cx="12" cy="12" r="5.5" fill="currentColor" /></svg>,
  list: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>,
  out: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true"><line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" /></svg>,
  site2: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>,
  panel: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="19" height="19" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="3" /><line x1="9" y1="4" x2="9" y2="20" /></svg>,
};

export default function CompaniesSidebar({
  open, companies, activeCompanyId, onSelect, onSettings, onDelete, onConvertToClient, onClearHistory,
  onCopyCurl, onVisitWebsite, pendingByCompany, sessionStatusByCompany, onRename, onAddCompany, onAddSample, onCloseSidebar,
}: Props) {
  const attentionByCompany = useLiveAttention();
  const compactCompany = useSession((s) => s.compactCompany);
  const unreadByCompany = useSession((s) => s.unreadByCompany) ?? {};
  const compactingByCompany = useSession((s) => s.compactionByCompany) ?? {};
  const chatActivity = useSession((s) => s.chatActivity) ?? {};
  const refreshChatActivity = useSession((s) => s.refreshChatActivity);
  const setChatFlag = useSession((s) => s.setChatFlag);
  const markChatUnread = useSession((s) => s.markChatUnread);
  const markChatSeen = useSession((s) => s.markChatSeen);
  const unreadHoldId = useSession((s) => s.unreadHoldId) ?? null;

  // Laptop: the panel's width (drag its right edge; null = the 300 px default). See resizeHandle().
  const [panelWidth, setPanelWidth] = useState<number | null>(() => readSidebarWidth(storage()));
  const [shownWidth, setShownWidth] = useState<number>(SIDEBAR_WIDTH.default);
  const resizeRef = useRef<{ x: number; start: number; width: number; frame: number; id: number } | null>(null);
  const sbRef = useRef<HTMLElement>(null);
  const [tab, setTab] = useState<SidebarTab>("clients");
  const [filter, setFilterState] = useState<SidebarFilter>(() => { const v = readPref(FILTER_KEY); return isSidebarFilter(v) ? v : "all"; });
  const [pinnedFolded, setPinnedFolded] = useState(() => readPref(PINNED_FOLD_KEY) === "1");
  const [pinnedExpanded, setPinnedExpanded] = useState(() => readPref(PINNED_EXPANDED_KEY) === "1");
  /** Show less was pressed: the next layout brings the folded block back into view. */
  const collapsingRef = useRef(false);
  /** The chat pinned last from this screen: it stays in view when Pinned is past its budget. */
  const [lastPinnedId, setLastPinnedId] = useState<string | null>(null);
  /** Rows are scrolling under the sticky Pinned head (it then shows its hairline). */
  const [pinHeadStuck, setPinHeadStuck] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [clock, setClock] = useState(() => Date.now());
  const [menu, setMenu] = useState<Menu | null>(null);
  const [undo, setUndo] = useState<Undo | null>(null);
  const viewSeqRef = useRef(0);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTyped, setDeleteTyped] = useState("");
  const [pressingId, setPressingId] = useState<string | null>(null);
  const [poppedId, setPoppedId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const undoTimerRef = useRef<number | null>(null);
  const popTimerRef = useRef<number | null>(null);
  // A long-press already did its job: the click the lift may send must not open the chat.
  const pressRef = useRef<{ timer: number; x: number; y: number; fired: boolean; id: string; pid: number } | null>(null);
  const lastPointerRef = useRef<string>("mouse");
  // Phone (WhatsApp-style): swipe a row left to show Pin · Archive · More. One row open at a time.
  const [swipedId, setSwipedId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const swipeRef = useRef<{ id: string; pid: number; x: number; y: number; base: number; width: number; axis: "x" | "y" | null; offset: number; el: HTMLElement;
    lastX: number; lastT: number; vx: number; frame: number; rowW: number; deep: boolean;
    /** Dragged right from a closed row (Mark as unread / read); armed = far enough to fire on release. */
    right?: boolean; armed?: boolean } | null>(null);
  const [movedIds, setMovedIds] = useState<ReadonlySet<string>>(() => new Set());
  const deepCommitRef = useRef(new Map<string, number>());
  const [closingId, setClosingId] = useState<string | null>(null);
  const closingTimerRef = useRef<number | null>(null);
  const prevSwipedRef = useRef<string | null>(null);
  const actionsDragRef = useRef(false);
  const swallowTapRef = useRef(false);
  const dialogRootRef = useRef<HTMLDivElement>(null);
  // The popover's real height (a sample menu is 11 items), measured on a hidden first pass before
  // paint, then placed: below its button, above, beside, or clamped (fixer r2 #2, Julian 10:30 d).
  const menuPanelRef = useRef<HTMLDivElement>(null);
  // Keyed to the open menu object (fixer r1 #8): every open measures afresh, so a menu that gained an
  // item since it last opened (Compact context) is never placed with its old height; and while it is
  // open, a change in its height (an item coming or going with a status poll) places it again. A
  // clamped panel (inline max-height) is left alone: its height is the clamp, not its content.
  const [menuMeasure, setMenuMeasure] = useState<{ menu: Menu; h: number } | null>(null);
  useLayoutEffect(() => {
    const panel = menuPanelRef.current;
    if (!menu || !panel) return;
    const h = panel.offsetHeight;
    if (h) setMenuMeasure((was) => (was?.menu === menu ? was : { menu, h }));
    if (typeof ResizeObserver !== "function") return;
    const watch = new ResizeObserver(() => {
      const now = panel.offsetHeight;
      if (!now || panel.style.maxHeight) return;
      setMenuMeasure((was) => (was?.menu === menu && Math.abs(was.h - now) < 1 ? was : { menu, h: now }));
    });
    watch.observe(panel);
    return () => watch.disconnect();
  }, [menu]);
  const dialogReturnFocusRef = useRef<HTMLElement | null>(null);
  const suppressDialogRestoreRef = useRef(false);
  /** Keyboard focus that came back to a row after a menu: if that row then leaves the list
   *  (archived) or remounts (pinned / unpinned), focus follows instead of falling to <body>. */
  const focusFollowRef = useRef<{ el: HTMLElement; id: string; after: HTMLElement[]; before: HTMLElement[]; until: number } | null>(null);

  useEffect(() => {
    const prev = prevSwipedRef.current;
    prevSwipedRef.current = swipedId;
    if (prev && prev !== swipedId) {
      setClosingId(prev);
      if (closingTimerRef.current) window.clearTimeout(closingTimerRef.current);
      closingTimerRef.current = window.setTimeout(() => setClosingId(null), SWIPE_SETTLE_MS + 40);
    }
  }, [swipedId]);

  const searching = Boolean(searchQuery.trim());
  const attentionCounts: Record<string, number> = {};
  for (const [id, a] of Object.entries(attentionByCompany ?? {})) if ((a?.count ?? 0) > 0) attentionCounts[id] = a!.count;
  const attentionSignature = Object.keys(attentionCounts).sort().join(",");
  // Chats with an agent running right now (the Working filter and its live count).
  const workingSignature = Object.entries(sessionStatusByCompany ?? {})
    .filter(([, s]) => isWorking(s)).map(([id]) => id).sort().join(",");
  const workingIds = useMemo(() => new Set(workingSignature ? workingSignature.split(",") : []), [workingSignature]);
  const inputs: SidebarInputs = {
    chats: companies, tab, activity: chatActivity, unread: unreadByCompany, attention: attentionCounts,
    query: searchQuery, archived: showArchived, activeId: activeCompanyId, movedIds, filter, working: workingIds,
  };
  const attention = useMemo(() => attentionChats(inputs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companies, tab, chatActivity, unreadByCompany, attentionSignature, searchQuery, showArchived, activeCompanyId, movedIds, filter]);
  const pinned = useMemo(() => pinnedChats(inputs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companies, tab, chatActivity, unreadByCompany, attentionSignature, searchQuery, showArchived, activeCompanyId, movedIds, filter]);
  const sections = useMemo(() => listSections(inputs, clock),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companies, tab, chatActivity, unreadByCompany, attentionSignature, searchQuery, showArchived, activeCompanyId, clock, movedIds, filter, workingIds]);
  const budget = usePinBudget(listRef, showArchived);
  const archivedTotal = countArchived(companies, tab);
  const tabCount = companies.filter((c) => (tab === "samples" ? c.kind === "sample" : c.kind !== "sample")).length;
  const clientsNeedAttention = Object.keys(attentionCounts).some((id) => companies.some((c) => c.id === id && c.kind !== "sample"));
  const unreadSignature = Object.keys(unreadByCompany).sort().join(",");
  const activeDialogKey = menu ? `menu:${menu.id}` : deletingId ? `delete:${deletingId}` : null;

  // Undo belongs to the view it was offered in: switching view or tab drops it.
  // A write started before the switch (deep-swipe slide-out, POST in flight) must not
  // offer its Undo in the new view: each switch bumps viewSeqRef, and offers carry theirs.
  function dropUndo() {
    viewSeqRef.current++;
    if (undoTimerRef.current) { window.clearTimeout(undoTimerRef.current); undoTimerRef.current = null; }
    setUndo(null);
  }
  function viewArchived(on: boolean) {
    dropUndo();
    setShowArchived(on);
    setSearchQuery("");
    setMenu(null);
    setSwipedId(null);
    if (listRef.current) listRef.current.scrollTop = 0;
    // The button pressed unmounts with the view: keep keyboard focus on the control that
    // switches back (Back in Archived, the Archived button in the list), never on <body>.
    const sidebar = listRef.current?.closest(".sb");
    const current = document.activeElement;
    if (sidebar && current && (current === document.body || sidebar.contains(current))) {
      window.requestAnimationFrame(() => {
        const now = document.activeElement;
        if (now && now !== document.body && now.isConnected && now !== current) return; // moved on already
        sidebar.querySelector<HTMLElement>(on ? ".sb__back" : ".sb__footerBtn")?.focus({ preventScroll: true });
      });
    }
  }

  // Recency + previews: on open, every 30 s while open and visible, and when a reply lands or work starts/stops.
  useEffect(() => {
    if (!open || typeof refreshChatActivity !== "function") return;
    void refreshChatActivity();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refreshChatActivity();
    }, ACTIVITY_POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, refreshChatActivity, unreadSignature, workingSignature, attentionSignature]);

  // The open chat scrolls into view — but Attention (revisions waiting) stays at the top (break-it r2:
  // on a phone with 6+ pins, opening the list from a chat in Today scrolled Attention off). Opening
  // the list shows Attention; the open chat is brought into view only when it fits under it.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!open) { wasOpenRef.current = false; return; }
    const opening = !wasOpenRef.current;
    wasOpenRef.current = true;
    const timer = window.setInterval(() => setClock(Date.now()), 60_000);
    const frame = window.requestAnimationFrame(() => {
      setClock(Date.now());
      const list = listRef.current;
      if (!list) return;
      const active = visibleOnly(Array.from(list.querySelectorAll<HTMLElement>(".sbRow.is-active, .sbTile.is-active")))[0];
      const att = list.querySelector<HTMLElement>(".sbAttention");
      if (!att) { active?.scrollIntoView({ block: "nearest", inline: "nearest" }); return; }
      const lr = list.getBoundingClientRect();
      const attTop = att.getBoundingClientRect().top - lr.top + list.scrollTop;
      if (opening && list.scrollTop > attTop) list.scrollTop = attTop;
      if (!active) return;
      const bottom = active.getBoundingClientRect().bottom - lr.top + list.scrollTop;
      if (bottom - attTop <= list.clientHeight) active.scrollIntoView({ block: "nearest", inline: "nearest" });
    });
    return () => { window.clearInterval(timer); window.cancelAnimationFrame(frame); };
  }, [open, activeCompanyId]);

  // The chosen width rides on :root as --sb-w-user; sidebar-shell.css clamps it to the window
  // (≥ 1100 px only — narrower screens keep their width, the phone list stays full screen) and the
  // chat, its header and the toast make room by the same variable (--sidebar-width).
  useEffect(() => {
    const rootStyle = typeof document !== "undefined" ? document.documentElement?.style : null;
    if (!rootStyle) return;
    if (panelWidth == null) rootStyle.removeProperty("--sb-w-user");
    else rootStyle.setProperty("--sb-w-user", `${panelWidth}px`);
  }, [panelWidth]);
  // What the panel really is in this window (the separator's value); a window resize re-reads it.
  useEffect(() => {
    const measure = () => setShownWidth(clampSidebarWidth(panelWidth ?? SIDEBAR_WIDTH.default, window.innerWidth));
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [panelWidth]);

  // Show less (the bottom line, the sticky head or the phone folder) after scrolling through every
  // pin: the block shrinks under a list that kept its scroll, so the pins were left scrolled off and
  // the sticky head cut a row in half (break-it r2 blocker). Before paint, the folded block comes
  // back into full view: from the top of the list (Attention + Pinned, the view the fold is sized
  // for) when that fits, else from the Pinned head.
  useLayoutEffect(() => {
    if (!collapsingRef.current) return;
    collapsingRef.current = false;
    if (pinnedExpanded) return;
    const list = listRef.current;
    const block = list?.querySelector<HTMLElement>(".sbPinned");
    if (!list || !block) return;
    const lr = list.getBoundingClientRect(), br = block.getBoundingClientRect();
    if (br.top >= lr.top - 0.5 && br.bottom <= lr.bottom + 0.5) return; // already whole
    const top = br.top - lr.top + list.scrollTop, bottom = br.bottom - lr.top + list.scrollTop;
    list.scrollTop = bottom <= list.clientHeight ? 0 : top;
  }, [pinnedExpanded]);

  // The sticky Pinned head gets its hairline only while rows scroll under it (julian-ui header rule).
  useEffect(() => {
    const list = listRef.current;
    if (!list || !open) return;
    let frame = 0;
    const check = () => {
      frame = 0;
      const head = list.querySelector<HTMLElement>(".sbPinned__head");
      if (!head) { setPinHeadStuck(false); return; }
      const section = head.parentElement!.getBoundingClientRect(), top = list.getBoundingClientRect().top;
      setPinHeadStuck(section.top < top - 0.5 && section.bottom > top + head.offsetHeight);
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(check); };
    list.addEventListener("scroll", onScroll, { passive: true });
    check();
    return () => { list.removeEventListener("scroll", onScroll); if (frame) window.cancelAnimationFrame(frame); };
  }, [open, pinnedExpanded, pinnedFolded, pinned.length]);

  useEffect(() => {
    if (!renamingId) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [renamingId]);

  // A different chat opened: close transient row state.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => { setMenu(null); setRenamingId(null); setDeletingId(null); });
    return () => window.cancelAnimationFrame(frame);
  }, [activeCompanyId]);

  useEffect(() => () => {
    if (undoTimerRef.current) window.clearTimeout(undoTimerRef.current);
    if (popTimerRef.current) window.clearTimeout(popTimerRef.current);
    if (pressRef.current) window.clearTimeout(pressRef.current.timer);
  }, []);

  // A gesture ends with the finger, not with the row node. A reply landing moves the row to another
  // section (a remount), so the old node never gets its pointerup: without this the 500 ms hold timer
  // pinned a quick tap, and a cut-off swipe stayed is-dragging. Runs after the row's own handlers
  // (window bubble), so a gesture they already ended is untouched (a fired hold keeps swallowing its click).
  useEffect(() => {
    function onEnd(e: PointerEvent) {
      const press = pressRef.current;
      if (press && !press.fired && press.pid === e.pointerId) {
        window.clearTimeout(press.timer); pressRef.current = null; setPressingId(null);
      }
      const swipe = swipeRef.current;
      if (!swipe || swipe.pid !== e.pointerId) return;
      swipeRef.current = null;
      if (swipe.frame) cancelAnimationFrame(swipe.frame);
      if (swipe.axis !== "x") return;
      const wrap = swipe.el.parentElement;
      for (const v of ["--sb-swipe-p", "--sb-swipe-x", "--sb-swipe-r"]) wrap?.style.removeProperty(v);
      wrap?.classList.remove("is-deep", "is-right", "is-armed");
      swipe.el.style.transform = ""; swipe.el.style.transition = ""; swipe.el.style.willChange = "";
      setDragId((id) => (id === swipe.id ? null : id));
    }
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    return () => { window.removeEventListener("pointerup", onEnd); window.removeEventListener("pointercancel", onEnd); };
  }, []);

  // An open row closes on any touch outside its actions.
  useEffect(() => {
    if (!swipedId) return;
    function onDown(e: PointerEvent) {
      const target = e.target as HTMLElement | null;
      const wrap = target?.closest?.(".sbSwipe") as HTMLElement | null;
      if (wrap?.dataset.chatId === swipedId) return;
      swallowTapRef.current = !!target?.closest?.(".sbRow");
      if (swallowTapRef.current) window.setTimeout(() => { swallowTapRef.current = false; }, 500);
      setSwipedId(null);
    }
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [swipedId]);
  useEffect(() => {
    if (open) return;
    const frame = window.requestAnimationFrame(() => setSwipedId(null));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);
  useEffect(() => {
    const list = listRef.current;
    if (!swipedId || !list) return;
    const onScroll = () => setSwipedId(null);
    list.addEventListener("scroll", onScroll, { passive: true });
    return () => list.removeEventListener("scroll", onScroll);
  }, [swipedId]);

  // Menus and confirmations: focus inside, Tab stays inside, Esc closes, focus returns.
  useEffect(() => {
    if (!activeDialogKey) return;
    const dialogRoot = dialogRootRef.current;
    if (!dialogRoot) return;
    const returnTarget = dialogReturnFocusRef.current;
    // Taken while the row is still in the list: by cleanup time an archive may already have removed it.
    const follow = focusFollowFor(returnTarget);
    const selector = "button:not(:disabled),[href],input:not(:disabled),[tabindex]:not([tabindex='-1'])";
    const focusable = () => Array.from(dialogRoot.querySelectorAll<HTMLElement>(selector))
      .filter((el) => !el.hidden && el.getAttribute("aria-hidden") !== "true" && el.getClientRects().length > 0);
    const frame = window.requestAnimationFrame(() => {
      const initial = dialogRoot.querySelector<HTMLElement>("[data-dialog-initial]") ?? focusable()[0] ?? dialogRoot;
      initial.focus({ preventScroll: true });
    });
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault(); e.stopPropagation();
        setMenu(null); setDeletingId(null);
        return;
      }
      if (activeDialogKey!.startsWith("menu:") && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        const items = focusable().filter((el) => el.matches(".sbMenu__item"));
        if (!items.length) return;
        e.preventDefault();
        const index = items.indexOf(document.activeElement as HTMLElement);
        const next = e.key === "ArrowDown" ? (index + 1) % items.length : (index - 1 + items.length) % items.length;
        items[next]?.focus({ preventScroll: true });
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusable();
      if (!items.length) { e.preventDefault(); dialogRoot!.focus({ preventScroll: true }); return; }
      const first = items[0], last = items[items.length - 1], current = document.activeElement;
      if (e.shiftKey && (current === first || !dialogRoot!.contains(current))) { e.preventDefault(); last.focus({ preventScroll: true }); }
      else if (!e.shiftKey && (current === last || !dialogRoot!.contains(current))) { e.preventDefault(); first.focus({ preventScroll: true }); }
    }
    document.addEventListener("keydown", onKey, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKey, true);
      if (suppressDialogRestoreRef.current) { suppressDialogRestoreRef.current = false; return; }
      focusFollowRef.current = follow && { ...follow, until: Date.now() + 10_000 };
      window.requestAnimationFrame(() => {
        if (returnTarget?.isConnected) returnTarget.focus({ preventScroll: true });
        else followLostFocus();
      });
    };
  }, [activeDialogKey]);

  function focusFollowFor(target: HTMLElement | null) {
    const rowEl = target?.closest?.<HTMLElement>(".sbRow[data-chat-id], .sbTile[data-chat-id]") ?? null;
    if (!rowEl?.isConnected || !rowEl.dataset.chatId) return null;
    const kindSel = rowEl.classList.contains("sbTile") ? ".sbTile[data-chat-id]" : ".sbRow[tabindex='0']";
    const rows = visibleOnly(Array.from(listRef.current?.querySelectorAll<HTMLElement>(kindSel) ?? []));
    const at = rows.indexOf(rowEl);
    const id = rowEl.dataset.chatId;
    const others = (list: HTMLElement[]) => list.filter((el) => el.dataset.chatId !== id);
    return { el: target!, id, after: others(at < 0 ? [] : rows.slice(at + 1)),
      before: others(at < 0 ? rows : rows.slice(0, at)).reverse(), until: 0 };
  }
  function followLostFocus() {
    const follow = focusFollowRef.current;
    if (!follow) return;
    if (Date.now() > follow.until) { focusFollowRef.current = null; return; }
    if (follow.el.isConnected) return; // still there (the write is in flight, or nothing moved)
    focusFollowRef.current = null;
    const current = document.activeElement;
    if (current && current !== document.body && current.isConnected) return; // focus already moved on
    const idSel = `[data-chat-id="${CSS.escape(follow.id)}"]`;
    const same = visibleOnly(Array.from(listRef.current?.querySelectorAll<HTMLElement>(`.sbRow[tabindex='0']${idSel}, .sbTile${idSel}`) ?? []))[0];
    const next = same ?? follow.after.find((el) => el.isConnected) ?? follow.before.find((el) => el.isConnected) ?? searchRef.current;
    next?.focus({ preventScroll: true });
  }
  useEffect(() => { followLostFocus(); });

  /** Laptop: the panel's right edge is a separator (Build's) — drag it (live, one frame at a time, no
   *  transitions), double-click it for the default, or focus it and use ← / → (Shift: bigger steps),
   *  Home / End. The choice is remembered on this device. */
  function setWidth(next: number | null) {
    const width = next == null ? null : clampSidebarWidth(next, window.innerWidth);
    setPanelWidth(width === SIDEBAR_WIDTH.default ? null : width);
    writeSidebarWidth(width, storage());
  }
  function endResize() {
    const drag = resizeRef.current;
    resizeRef.current = null;
    if (drag?.frame) cancelAnimationFrame(drag.frame);
    document.documentElement.classList.remove("sb-resizing");
    return drag;
  }
  function resizeHandle() {
    return <div className="sb__resize" role="separator" aria-orientation="vertical" tabIndex={0}
      aria-label="Resize chats panel" aria-valuemin={SIDEBAR_WIDTH.min}
      aria-valuemax={clampSidebarWidth(SIDEBAR_WIDTH.max, typeof window !== "undefined" ? window.innerWidth : 1440)}
      aria-valuenow={shownWidth} title="Drag to resize · double-click to reset"
      onPointerDown={(e) => {
        if (e.pointerType === "touch" || e.button !== 0) return;
        e.preventDefault();
        const start = sbRef.current?.getBoundingClientRect().width ?? shownWidth;
        resizeRef.current = { x: e.clientX, start, width: start, frame: 0, id: e.pointerId };
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* fine */ }
        document.documentElement.classList.add("sb-resizing");
      }}
      onPointerMove={(e) => {
        const drag = resizeRef.current;
        if (!drag || drag.id !== e.pointerId) return;
        drag.width = clampSidebarWidth(drag.start + (e.clientX - drag.x), window.innerWidth);
        if (!drag.frame) drag.frame = requestAnimationFrame(() => {
          drag.frame = 0;
          document.documentElement.style.setProperty("--sb-w-user", `${drag.width}px`);
        });
      }}
      onPointerUp={(e) => {
        if (resizeRef.current?.id !== e.pointerId) return;
        const drag = endResize()!;
        if (Math.abs(drag.width - drag.start) >= 1) setWidth(drag.width);
        else if (panelWidth == null) document.documentElement.style.removeProperty("--sb-w-user");
        else document.documentElement.style.setProperty("--sb-w-user", `${panelWidth}px`);
      }}
      onPointerCancel={() => {
        endResize();
        if (panelWidth == null) document.documentElement.style.removeProperty("--sb-w-user");
        else document.documentElement.style.setProperty("--sb-w-user", `${panelWidth}px`);
      }}
      onDoubleClick={() => setWidth(null)}
      onKeyDown={(e) => {
        const step = e.shiftKey ? SIDEBAR_WIDTH.bigStep : SIDEBAR_WIDTH.step;
        const next = e.key === "ArrowLeft" ? shownWidth - step : e.key === "ArrowRight" ? shownWidth + step
          : e.key === "Home" ? SIDEBAR_WIDTH.min : e.key === "End" ? SIDEBAR_WIDTH.max : null;
        if (next == null) return;
        e.preventDefault();
        setWidth(next);
      }} />;
  }

  function switchTab(next: SidebarTab) {
    if (next === tab) return;
    if (listRef.current) listRef.current.scrollTop = 0;
    dropUndo();
    setSwipedId(null);
    setTab(next);
  }

  function setFilter(next: SidebarFilter) {
    if (next === filter) return;
    applyFilter(next);
  }
  /** A Filter option: the one in use again (Unread or Pinned) goes back to All chats. */
  function chooseFilter(key: SidebarFilter) {
    applyFilter(key === filter && key !== "all" ? "all" : key);
  }
  function applyFilter(next: SidebarFilter) {
    if (next === filter) return;
    dropUndo();
    setSwipedId(null);
    setFilterState(next);
    writePref(FILTER_KEY, next);
    if (listRef.current) listRef.current.scrollTop = 0;
  }
  function togglePinnedFold() {
    setPinnedFolded((was) => { writePref(PINNED_FOLD_KEY, was ? "0" : "1"); return !was; });
  }
  /** Show every pin / go back to the budget. From the keyboard, expanding lands on the first pin that
   *  came into view (a mouse click leaves focus where it is: a focused pinned row would show its Unpin
   *  and ⋯ with the mouse elsewhere, fixer r1 #2). Folding back lands on the Show line or the folder,
   *  whichever this screen shows (the head's Show less unmounts with the fold, fixer r1 #3). */
  function setPinsExpanded(next: boolean, from?: HTMLElement | null, firstNewId?: string | null, byKeyboard = false) {
    if (!next) collapsingRef.current = true;
    setPinnedExpanded(next);
    writePref(PINNED_EXPANDED_KEY, next ? "1" : "0");
    if (!from || document.activeElement !== from) return;
    if (next && !byKeyboard) return;
    window.requestAnimationFrame(() => {
      const scope = listRef.current?.querySelector<HTMLElement>(".sbPinned");
      if (!scope) return;
      const sel = next && firstNewId
        ? `.sbRow[data-place="pinned"][data-chat-id="${CSS.escape(firstNewId)}"], .sbTile[data-chat-id="${CSS.escape(firstNewId)}"]`
        : ".sbPinMore, .sbTile--more";
      const target = visibleOnly(Array.from(scope.querySelectorAll<HTMLElement>(sel)))[0];
      if (target && target !== document.activeElement) target.focus({ preventScroll: false });
    });
  }
  /** Unread as the row shows it: the open chat only when it was marked unread by hand. */
  function shownUnread(chat: Company): boolean {
    return (!!unreadByCompany[chat.id] || !!pendingByCompany?.[chat.id]) && (chat.id !== activeCompanyId || unreadHoldId === chat.id);
  }
  function toggleUnread(chat: Company, source: string) {
    if (shownUnread(chat)) { if (typeof markChatSeen === "function") markChatSeen(chat.id); }
    else if (typeof markChatUnread === "function") void markChatUnread(chat.id, source);
  }

  function startRename(chat: Company) {
    setMenu(null);
    setDeletingId(null);
    setRenameDraft(chat.name);
    setRenamingId(chat.id);
  }
  function commitRename(id: string) {
    const next = renameDraft.trim();
    if (next && next !== companies.find((c) => c.id === id)?.name) onRename(id, next);
    endRename(id);
  }
  /** Close the rename field; keyboard focus goes back to the same row (break-it r4),
   *  unless the blur was a click that already moved focus somewhere else. A rename
   *  started by touch (swipe / sheet) just lets go of focus: typing in the field counts
   *  as keyboard use, so focusing the row would leave a focus ring on a phone (final r1 #3). */
  function endRename(id: string) {
    const input = renameInputRef.current;
    const place = input?.closest<HTMLElement>(".sbRow")?.dataset.place;
    const byTouch = lastPointerRef.current === "touch";
    setRenamingId(null);
    if (byTouch) return; // the field unmounts; never blur() it here (its onBlur would commit again)
    window.requestAnimationFrame(() => {
      const current = document.activeElement;
      if (current && current !== document.body && current !== input && current.isConnected) return;
      const sel = `.sbRow[tabindex='0'][data-chat-id="${CSS.escape(id)}"]`;
      const el = (place ? listRef.current?.querySelector<HTMLElement>(`${sel}[data-place="${CSS.escape(place)}"]`) : null)
        ?? listRef.current?.querySelector<HTMLElement>(sel);
      el?.focus({ preventScroll: true });
    });
  }

  function offerUndo(next: Undo) {
    if (undoTimerRef.current) window.clearTimeout(undoTimerRef.current);
    setUndo(next);
    undoTimerRef.current = window.setTimeout(() => { setUndo(null); undoTimerRef.current = null; }, UNDO_MS);
  }

  async function setArchived(chat: Company, archived: boolean, source = "menu", viewSeq = viewSeqRef.current): Promise<boolean> {
    if (typeof setChatFlag !== "function") return false;
    if (undoTimerRef.current) { window.clearTimeout(undoTimerRef.current); undoTimerRef.current = null; }
    setUndo(null);
    const wasPinned = isPinned(chat);
    const wasPinnedAt = wasPinned ? chat.pinnedAt ?? null : null;
    const already = isArchived(chat) === archived; // nothing changes: never offer an Undo for it
    const ok = await setChatFlag(chat.id, "archived", archived, source);
    if (ok) setMovedIds((prev) => { const next = new Set(prev); next.add(chat.id); return next; });
    if (ok && archived && !already && viewSeq === viewSeqRef.current) offerUndo({ kind: "archive", id: chat.id, name: chat.name, pinned: wasPinned, pinnedAt: wasPinnedAt });
    return !!ok;
  }

  /** Pin / unpin. A hold on a phone and the laptop pin button offer Undo (the change is shared). */
  function togglePin(chat: Company, source: string, withUndo: boolean) {
    if (typeof setChatFlag !== "function") return;
    const pinning = !isPinned(chat);
    const pinnedAt = pinning ? null : chat.pinnedAt ?? null;
    const offered: Undo | null = withUndo ? { kind: pinning ? "pin" : "unpin", id: chat.id, name: chat.name, pinned: !pinning, pinnedAt } : null;
    void Promise.resolve(setChatFlag(chat.id, "pinned", pinning, source)).then((ok) => {
      // A write that failed was rolled back: its Undo would undo nothing (and fail again).
      if (!ok && offered) setUndo((u) => (u === offered ? null : u));
    });
    if (offered) offerUndo(offered);
    if (pinning) setLastPinnedId(chat.id);
    setPoppedId(chat.id);
    if (popTimerRef.current) window.clearTimeout(popTimerRef.current);
    popTimerRef.current = window.setTimeout(() => setPoppedId(null), 420);
  }

  function openChatMenu(chat: Company, anchorEl: unknown, point?: { x: number; y: number }, place?: string) {
    dialogReturnFocusRef.current = (anchorEl as HTMLElement) ?? null;
    setMenu({ id: chat.id, anchor: rectOf(anchorEl), x: point?.x, y: point?.y, place });
  }

  function focusSibling(from: HTMLElement, delta: number) {
    const rows = visibleOnly(Array.from(listRef.current?.querySelectorAll<HTMLElement>(ROVING) ?? []));
    rows[rows.indexOf(from) + delta]?.focus();
  }

  /**
   * After a hold fired, the click the lift may send lands on whatever is under the finger now —
   * often the Undo the hold just offered. Swallow that one click anywhere (capture phase), until
   * the next touch starts or shortly after the lift.
   */
  function swallowLiftClick(pid: number) {
    let timer = 0;
    const done = () => {
      window.clearTimeout(timer);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("pointerup", onUp, true);
      document.removeEventListener("pointercancel", onUp, true);
    };
    const onClick = (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); done(); };
    const onDown = (e: PointerEvent) => { if (e.pointerId !== pid) done(); };
    const onUp = (e: PointerEvent) => { if (e.pointerId === pid) { window.clearTimeout(timer); timer = window.setTimeout(done, 600); } };
    document.addEventListener("click", onClick, true);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("pointerup", onUp, true);
    document.addEventListener("pointercancel", onUp, true);
    timer = window.setTimeout(done, 10_000); // a lost pointerup never leaves it armed
  }

  function cancelPress() {
    const press = pressRef.current;
    if (press && !press.fired) { window.clearTimeout(press.timer); pressRef.current = null; }
    setPressingId(null);
  }

  /** One chat row. `compact` = a pinned row in the Pinned block: one line (name + one state trail),
   *  the same element, gestures and keys as every row. */
  function renderRow(chat: Company, place: "attention" | "pinned" | "list", compact = false) {
    const isActive = chat.id === activeCompanyId;
    const isRenaming = renamingId === chat.id;
    const activity: ChatActivity | undefined = chatActivity[chat.id];
    const status = sessionStatusByCompany?.[chat.id];
    const unread = shownUnread(chat);
    const state: RowState = rowState({ status, unread, isActive: isActive && unreadHoldId !== chat.id });
    const att = attentionByCompany?.[chat.id];
    const attCount = att?.count ?? 0;
    const revisionWorking = att?.items?.some((it) => it.runtimeState === "running") ?? false;
    const working = state.tone === "working" || state.tone === "starting" || (place === "attention" && revisionWorking);
    const at = lastActivityMs(chat, activity, unreadByCompany[chat.id]);
    const preview = previewText(activity);
    const kind = chatKind(chat);
    const menuOpen = menu?.id === chat.id;
    const archivedNow = isArchived(chat);
    // The open chat stays in the main list after it is archived; an archived client stays in Attention.
    const showArchivedTag = archivedNow && !showArchived;
    const swipeActions = archivedNow ? 2 : 3;
    const swipeOpen = swipedId === chat.id;
    const swipeLive = swipeOpen || dragId === chat.id || closingId === chat.id;
    const autoOn = chat.autoRevision === true;

    function dragStep(swipe: NonNullable<typeof swipeRef.current>, clientX: number, timeStamp: number) {
      const raw = swipe.base + (clientX - swipe.x);
      // From a closed row, a drag to the right reveals Unread / Read on the left (WhatsApp).
      if (swipe.base === 0 && (raw > 0 || swipe.right)) {
        swipe.right = true;
        const max = Math.min(160, swipe.rowW * 0.5);
        const pull = Math.max(0, raw);
        swipe.offset = pull <= UNREAD_ARM_PX ? pull : Math.min(max, UNREAD_ARM_PX + (pull - UNREAD_ARM_PX) * 0.45);
        const armed = pull >= UNREAD_ARM_PX;
        swipe.lastX = clientX; swipe.lastT = timeStamp;
        if (!swipe.frame) swipe.frame = requestAnimationFrame(() => {
          swipe.frame = 0;
          swipe.el.style.transition = "none";
          swipe.el.style.willChange = "transform";
          swipe.el.style.transform = `translate3d(${swipe.offset}px,0,0)`;
          const wrap = swipe.el.parentElement;
          wrap?.style.setProperty("--sb-swipe-r", `${swipe.offset}px`);
          wrap?.classList.add("is-right");
          if (wrap && swipe.armed !== armed) { wrap.classList.toggle("is-armed", armed); if (armed) vibrate(10); }
          swipe.armed = armed;
        });
        return;
      }
      swipe.offset = raw > 0 ? raw * 0.2 : raw < -swipe.width ? Math.max(-swipe.rowW, -swipe.width + (raw + swipe.width) * 0.85) : raw;
      // The drag stops at -rowW, so the deep line must sit inside the row (a 280 px list has rowW ≈ 263
      // and 3 actions ≈ 228 → max(276, 163) was unreachable). Keep it clear of the open position too.
      swipe.deep = swipe.offset < -Math.max(swipe.width + 8, Math.min(swipe.rowW - 16, Math.max(swipe.width + 48, swipe.rowW * SWIPE_DEEP_SHARE)))
        // When the actions fill most of the row (landscape phone: 228 of 263 px) the line above needs ~95%
        // of the row in finger travel. From a closed row, 80% of the row's width is deep enough.
        || (swipe.base === 0 && swipe.rowW - swipe.width < 60 && raw < -swipe.rowW * 0.8);
      const dt = Math.max(1, timeStamp - swipe.lastT);
      swipe.vx = swipe.vx * 0.4 + ((clientX - swipe.lastX) / dt) * 0.6;
      swipe.lastX = clientX; swipe.lastT = timeStamp;
      if (!swipe.frame) swipe.frame = requestAnimationFrame(() => {
        swipe.frame = 0;
        swipe.el.style.transition = "none";
        swipe.el.style.willChange = "transform";
        swipe.el.style.transform = `translate3d(${swipe.offset}px,0,0)`;
        const wrap = swipe.el.parentElement;
        wrap?.style.setProperty("--sb-swipe-p", String(Math.max(0, Math.min(1, -swipe.offset / swipe.width))));
        wrap?.style.setProperty("--sb-swipe-x", `${Math.max(0, -swipe.offset)}px`);
        if (wrap && wrap.classList.contains("is-deep") !== swipe.deep) {
          wrap.classList.toggle("is-deep", swipe.deep);
          if (swipe.deep) vibrate(10);
        }
      });
    }

    function endSwipe(row: HTMLElement) {
      const swipe = swipeRef.current;
      swipeRef.current = null;
      if (!swipe || swipe.axis !== "x") return;
      if (swipe.frame) cancelAnimationFrame(swipe.frame);
      if (swipe.right) {
        const wrap = row.parentElement;
        // Snap back from where the finger let go, then fire (the row never stays open to the right).
        row.style.transition = ""; row.style.transform = ""; row.style.willChange = "";
        window.setTimeout(() => { wrap?.classList.remove("is-right", "is-armed"); wrap?.style.removeProperty("--sb-swipe-r"); }, SWIPE_SETTLE_MS);
        swallowTapRef.current = true;
        window.setTimeout(() => { swallowTapRef.current = false; }, 400);
        if (swipe.armed || Math.max(0, swipe.offset) >= UNREAD_ARM_PX) toggleUnread(chat, "swipe-right");
        setDragId(null);
        return;
      }
      if (swipe.deep) { commitDeep(row); return; }
      const openIt = swipe.vx < -SWIPE_FLICK ? true : swipe.vx > SWIPE_FLICK ? false : swipe.offset < -swipe.width * 0.4;
      const wrap = row.parentElement;
      wrap?.style.removeProperty("--sb-swipe-p");
      wrap?.style.removeProperty("--sb-swipe-x");
      wrap?.classList.remove("is-deep");
      row.style.transform = ""; row.style.transition = ""; row.style.willChange = "";
      swallowTapRef.current = true;
      window.setTimeout(() => { swallowTapRef.current = false; }, 400);
      setSwipedId(openIt ? chat.id : null);
      setDragId(null);
    }

    /** Deep swipe let go: the row slides out, the gap closes, then it archives (in the
     *  Archived view: unarchives). Undo stays available; on failure the row comes back. */
    function commitDeep(row: HTMLElement) {
      const wrap = row.parentElement as HTMLElement | null;
      swallowTapRef.current = true;
      window.setTimeout(() => { swallowTapRef.current = false; }, 600);
      setSwipedId(null);
      // The direction matches the revealed actions: an archived row (the Archived view, an archived
      // client kept in Attention, the open archived chat kept in the list) unarchives. One commit per row at a time.
      const archiveIt = !showArchived && !archivedNow;
      const viewSeq = viewSeqRef.current; // the view this gesture started in
      // Dedupe per row AND direction: a doubled release is ignored, an unarchive right after an archive is not.
      const dedupeKey = `${chat.id}:${archiveIt ? "archive" : "unarchive"}`;
      const last = deepCommitRef.current.get(dedupeKey) ?? 0;
      if (Date.now() - last < DEEP_DEDUPE_MS) { setDragId(null); return; }
      deepCommitRef.current.set(dedupeKey, Date.now());
      const source = archiveIt ? "deep-swipe" : "archived-deep";
      // An archived client in Attention stays there (revisions never hide), and an archived row in the
      // main list stays once unarchived: no slide-out, just the flag.
      if (!wrap || place === "attention" || (!archiveIt && !showArchived)) {
        row.style.transform = ""; row.style.transition = "";
        wrap?.classList.remove("is-deep");
        void setArchived(chat, archiveIt, source, viewSeq); setDragId(null); return;
      }
      wrap.classList.add("is-deep", "is-leaving");
      wrap.style.setProperty("--sb-swipe-x", `${row.getBoundingClientRect().width}px`);
      row.style.transition = `transform ${DEEP_OUT_MS}ms cubic-bezier(0.4, 0, 1, 1)`;
      row.style.transform = "translate3d(-100%,0,0)";
      window.setTimeout(() => {
        wrap.style.height = `${wrap.getBoundingClientRect().height}px`;
        void wrap.offsetHeight;
        wrap.style.transition = `height ${DEEP_COLLAPSE_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${DEEP_COLLAPSE_MS}ms ease`;
        wrap.style.height = "0px";
        wrap.style.opacity = "0";
        window.setTimeout(async () => {
          const ok = await setArchived(chat, archiveIt, source, viewSeq);
          if (!ok) {
            for (const p of ["height", "opacity", "transition", "--sb-swipe-x", "--sb-swipe-p"]) wrap.style.removeProperty(p);
            wrap.classList.remove("is-deep", "is-leaving");
            row.style.transform = ""; row.style.transition = "";
          }
          setDragId(null);
        }, DEEP_COLLAPSE_MS);
      }, DEEP_OUT_MS);
    }

    let line2: React.ReactNode;
    if (place === "attention") {
      line2 = <span className={`sbRow__state ${revisionWorking ? "is-working" : "is-needs"}`}>
        {revisionWorking ? "Working on a revision" : `${attCount} revision${attCount === 1 ? "" : "s"} waiting`}
      </span>;
    } else if (state.tone === "working") {
      line2 = <span className="sbRow__state is-working"><WorkingWord /></span>;
    } else if (state.label) {
      line2 = <span className={`sbRow__state is-${state.tone}`}>{state.label}</span>;
    } else if (working) {
      line2 = <span className="sbRow__state is-working"><WorkingWord /></span>;
    } else {
      line2 = <span className="sbRow__preview">
        {preview ? highlight(preview, searchQuery) : <span className="sbRow__quiet">{quietLine(chat, clock)}</span>}
      </span>;
    }

    // A compact pinned row's one trail, highest state wins (julian-ui nav row): Needs you › working ›
    // starting › unread (blue time + dot) › the time. It keeps its room on hover, so the name never reflows.
    let trail: React.ReactNode = null;
    if (compact) {
      if (state.tone === "needs") trail = <span className="sbTrail is-needs"><span className="sbDot is-needs" aria-hidden="true" />Needs you</span>;
      else if (state.tone === "working") trail = <span className="sbTrail is-working"><WorkingOrb /><WorkingWord /></span>;
      else if (state.tone === "starting") trail = <span className="sbTrail is-working"><WorkingOrb /><span className="sbVerb">Starting…</span></span>;
      else trail = <span className={`sbTrail ${state.tone === "unread" ? "is-unread" : ""}`}>
        <time className="sbRow__time" dateTime={at ? new Date(at).toISOString() : undefined}>{formatRowTime(at, clock)}</time>
        {state.tone === "unread" && <span className="sbDot is-unread" role="img" aria-label="New reply" title="New reply you haven't opened" />}
      </span>;
    }
    const stateWord = state.tone === "working" ? "Working" : state.tone === "starting" ? "Starting" : state.tone === "needs" ? "Needs you" : state.tone === "unread" ? "New reply" : "";

    return (
      <div key={`${place}-${chat.id}`}
        className={`sbSwipe ${swipeOpen ? "is-open" : ""} ${dragId === chat.id ? "is-dragging" : ""} ${closingId === chat.id && !swipeOpen && dragId !== chat.id ? "is-closing" : ""} ${isRenaming ? "is-renaming" : ""}`}
        data-chat-id={chat.id} data-no-swipe="true"
        style={{ "--sb-swipe-w": `${swipeActions * SWIPE_ACTION_W}px` } as React.CSSProperties}>
        <div className="sbSwipe__lead" aria-hidden="true">
          {unread ? I.read : I.unread}<span>{unread ? "Read" : "Unread"}</span>
        </div>
        {swipeLive && (
          <div className="sbSwipe__actions" aria-hidden={!swipeOpen}
            onPointerDown={(e) => {
              if (e.pointerType !== "touch" || !swipeOpen) return;
              const row = e.currentTarget.parentElement?.querySelector<HTMLElement>(":scope > .sbRow");
              if (!row) return;
              const width = swipeActions * SWIPE_ACTION_W;
              actionsDragRef.current = false;
              swipeRef.current = { id: chat.id, pid: e.pointerId, x: e.clientX, y: e.clientY, base: -width, width, axis: null, offset: -width, el: row,
                lastX: e.clientX, lastT: e.timeStamp, vx: 0, frame: 0, rowW: row.getBoundingClientRect().width, deep: false };
            }}
            onPointerMove={(e) => {
              const swipe = swipeRef.current;
              if (!swipe || swipe.id !== chat.id || e.pointerType !== "touch") return;
              const dx = e.clientX - swipe.x, dy = e.clientY - swipe.y;
              if (!swipe.axis) {
                if (Math.abs(dy) > SWIPE_SLOP && Math.abs(dy) >= Math.abs(dx)) { swipe.axis = "y"; return; }
                if (Math.abs(dx) <= SWIPE_SLOP || Math.abs(dx) <= Math.abs(dy)) return;
                swipe.axis = "x";
                actionsDragRef.current = true;
                try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* fine */ }
                setDragId(chat.id);
              }
              if (swipe.axis === "x") dragStep(swipe, e.clientX, e.timeStamp);
            }}
            onPointerUp={() => { const swipe = swipeRef.current; if (swipe?.id === chat.id && swipe.axis === "x") endSwipe(swipe.el); else swipeRef.current = null; }}
            onPointerCancel={() => { const swipe = swipeRef.current; if (swipe?.id === chat.id) endSwipe(swipe.el); }}
            onClickCapture={(e) => { if (actionsDragRef.current) { e.stopPropagation(); e.preventDefault(); actionsDragRef.current = false; } }}>
            {!archivedNow && (
              <button type="button" className="sbSwipe__btn is-pin" tabIndex={swipeOpen ? 0 : -1}
                onClick={() => { setSwipedId(null); togglePin(chat, "swipe-btn", place === "attention"); }}>
                {isPinned(chat) ? I.unpin : I.pin}
                <span>{isPinned(chat) ? "Unpin" : "Pin"}</span>
              </button>
            )}
            <button type="button" className="sbSwipe__btn is-archive" tabIndex={swipeOpen ? 0 : -1}
              onClick={() => { setSwipedId(null); void setArchived(chat, !archivedNow, "swipe-btn"); }}>
              {I.archive}
              <span>{archivedNow ? "Unarchive" : "Archive"}</span>
            </button>
            <button type="button" className="sbSwipe__btn is-more" tabIndex={swipeOpen ? 0 : -1}
              onClick={(e) => { const row = e.currentTarget.closest(".sbSwipe")?.querySelector(".sbRow"); setSwipedId(null); openChatMenu(chat, row ?? e.currentTarget, undefined, place); }}>
              {I.more}
              <span>More</span>
            </button>
          </div>
        )}
        <div
          className={`sbRow ${compact ? "is-compact" : ""} ${isActive ? "is-active" : ""} ${isRenaming ? "is-editing" : ""} ${state.tone !== "none" ? `is-${state.tone}` : ""} ${place === "attention" ? "is-attention" : ""} ${working ? "is-live" : ""} ${menuOpen ? "is-menu" : ""} ${pressingId === chat.id ? "is-pressing" : ""} ${poppedId === chat.id ? "is-popped" : ""}`}
          data-chat-id={chat.id}
          data-place={place}
          onClick={(e) => {
            if (isRenaming) return;
            if (pressRef.current?.fired) { pressRef.current = null; return; }
            if (swallowTapRef.current) { swallowTapRef.current = false; return; }
            if (swipeOpen) { setSwipedId(null); return; }
            const target = e.target as HTMLElement;
            if (target.closest?.(".sbRow__more, .sbRow__pinBtn")) return;
            onSelect(chat.id);
          }}
          onContextMenu={(e) => {
            // The rename field keeps the browser's own Cut/Copy/Paste menu (right-click or Android long-press).
            if (isRenaming || (e.target as HTMLElement).closest?.(".sbRow__rename")) return;
            // A hold on a touch screen must never show the browser's callout or our menu (it pins instead).
            e.preventDefault();
            if (lastPointerRef.current === "touch") return;
            openChatMenu(chat, e.currentTarget, { x: e.clientX, y: e.clientY }, place);
          }}
          onPointerDown={(e) => {
            lastPointerRef.current = e.pointerType;
            if (pressRef.current?.fired) pressRef.current = null; // a finished hold never swallows a later tap
            if (e.pointerType !== "touch" || isRenaming) return;
            const row = e.currentTarget;
            const width = swipeActions * SWIPE_ACTION_W;
            swipeRef.current = { id: chat.id, pid: e.pointerId, x: e.clientX, y: e.clientY, base: swipeOpen ? -width : 0, width, axis: null, offset: swipeOpen ? -width : 0, el: row,
              lastX: e.clientX, lastT: e.timeStamp, vx: 0, frame: 0, rowW: row.getBoundingClientRect().width, deep: false };
            if (swipeOpen) return; // a hold on an open row just closes it
            // A hold pins a chat from the list. Everywhere else it opens the chat's sheet, as a hold on a
            // phone tile does (break-it r2): a pinned row (iPad / landscape) must never unpin for everyone
            // by accident, an Attention row shows no pin state, and an archived chat is not pinned
            // (Unarchive first). Before, a hold there did nothing and the lift opened the chat.
            const sheet = place !== "list" || archivedNow;
            const press = { x: e.clientX, y: e.clientY, fired: false, id: chat.id, pid: e.pointerId, timer: 0 };
            press.timer = window.setTimeout(() => {
              if (pressRef.current !== press) return;
              press.fired = true;
              swallowLiftClick(press.pid);
              setPressingId(null);
              try { window.getSelection()?.removeAllRanges(); } catch { /* nothing selected */ }
              vibrate(12);
              if (sheet) openChatMenu(chat, row, undefined, place);
              else togglePin(chat, "long-press", true);
            }, LONG_PRESS_MS);
            pressRef.current = press;
            setPressingId(chat.id);
          }}
          onPointerMove={(e) => {
            const press = pressRef.current;
            if (press && !press.fired && Math.hypot(e.clientX - press.x, e.clientY - press.y) > PRESS_SLOP) cancelPress();
            const swipe = swipeRef.current;
            if (!swipe || swipe.id !== chat.id || press?.fired) return;
            const dx = e.clientX - swipe.x, dy = e.clientY - swipe.y;
            if (!swipe.axis) {
              if (Math.abs(dy) > SWIPE_SLOP && Math.abs(dy) >= Math.abs(dx)) { swipe.axis = "y"; return; }
              if (Math.abs(dx) <= SWIPE_SLOP || Math.abs(dx) <= Math.abs(dy)) return;
              swipe.axis = "x";
              cancelPress();
              try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* fine */ }
              if (swipedId && swipedId !== chat.id) setSwipedId(null);
              setDragId(chat.id);
            }
            if (swipe.axis !== "x") return;
            dragStep(swipe, e.clientX, e.timeStamp);
          }}
          onPointerUp={(e) => {
            const press = pressRef.current;
            if (press?.fired) { swipeRef.current = null; return; } // the hold pinned; the click that follows is swallowed
            cancelPress();
            endSwipe(e.currentTarget);
          }}
          onPointerCancel={(e) => {
            cancelPress();
            if (pressRef.current?.fired) { swipeRef.current = null; return; }
            endSwipe(e.currentTarget);
          }}
          onPointerLeave={() => { if (pressRef.current && !pressRef.current.fired) cancelPress(); }}
          role="button"
          tabIndex={isRenaming ? -1 : 0}
          aria-label={`${chat.name}${place === "attention" ? `, ${attCount} revision${attCount === 1 ? "" : "s"} waiting` : state.a11y ? `, ${state.a11y}` : ""}${isPinned(chat) ? ", pinned" : ""}${archivedNow ? ", archived" : ""}`}
          aria-current={isActive ? "true" : undefined}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget || isRenaming) return;
            if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); focusSibling(e.currentTarget, e.key === "ArrowDown" ? 1 : -1); return; }
            if ((e.key === "F10" && e.shiftKey) || e.key === "ContextMenu") { e.preventDefault(); openChatMenu(chat, e.currentTarget, undefined, place); return; }
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            onSelect(chat.id);
          }}
          title={[`${chat.name} · ${KIND_LABEL[kind]}`, compact ? stateWord : "", at ? `Last active ${formatExactTime(at, clock)}` : ""].filter(Boolean).join("\n")}
        >
          <Face id={chat.id} working={working} size={compact ? "var(--sb-pin-face, 24px)" : undefined} />
          <div className="sbRow__main">
            {isRenaming ? (
              <input
                ref={renameInputRef}
                type="text"
                className="sbRow__rename"
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); commitRename(chat.id); }
                  else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); endRename(chat.id); }
                }}
                onBlur={() => commitRename(chat.id)}
                onClick={(e) => e.stopPropagation()}
                aria-label="Rename"
              />
            ) : compact ? (
              <div className="sbRow__line1">
                <span className="sbRow__title">{chat.name}</span>
                {trail}
              </div>
            ) : (
              <div className="sbRow__line1">
                <span className="sbRow__title">{highlight(chat.name, searchQuery)}</span>
                {place === "attention" && attCount > 0 && <span className="sbCount" aria-hidden="true">{attCount}</span>}
                <time className="sbRow__time" dateTime={at ? new Date(at).toISOString() : undefined}>{formatRowTime(at, clock)}</time>
              </div>
            )}
            {!compact && <div className="sbRow__line2">
              {/* A pinned chat shown outside Pinned (Attention, search, the Unread filter) keeps a small pin mark. */}
              {place !== "pinned" && isPinned(chat) && <span className="sbRow__pinMark" title="Pinned">{I.pinMark}</span>}
              {showArchivedTag && <span className="sbTag" title="Archived — hidden from the list for everyone">Archived</span>}
              {kind === "chat" && place !== "attention" && <span className="sbTag" title="This sample chat has no website yet">No site yet</span>}
              {place === "attention" && autoOn && <span className="sbTag" title="Auto-approve is on for this client">Auto</span>}
              {working && <WorkingOrb />}
              {line2}
              {state.tone === "needs" && <span className="sbDot is-needs" role="img" aria-label="Needs you" />}
              {state.tone === "unread" && place !== "attention" && <span className="sbDot is-unread" role="img" aria-label="New reply" title="New reply you haven't opened" />}
            </div>}
          </div>
          {!isRenaming && !archivedNow && (
            <button type="button" className={`sbRow__pinBtn ${isPinned(chat) ? "is-on" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                // The row remounts (into / out of Pinned): focus follows it, never drops to <body>.
                if (document.activeElement === e.currentTarget) {
                  const follow = focusFollowFor(e.currentTarget);
                  focusFollowRef.current = follow && { ...follow, until: Date.now() + 3_000 };
                }
                togglePin(chat, "pin-btn", true);
              }}
              aria-label={isPinned(chat) ? `Unpin ${chat.name}` : `Pin ${chat.name} to top`}
              title={isPinned(chat) ? "Unpin" : "Pin to top"}>
              {isPinned(chat) ? I.unpin : I.pin}
            </button>
          )}
          {!isRenaming && (
            <button type="button" className="sbRow__more"
              onClick={(e) => { e.stopPropagation(); if (menuOpen) { setMenu(null); return; } openChatMenu(chat, e.currentTarget, undefined, place); }}
              aria-label={`Actions for ${chat.name}`} aria-haspopup="dialog" aria-expanded={menuOpen} title="More">
              {I.more}
            </button>
          )}
        </div>
      </div>
    );
  }

  /** A pinned chat's state for counts and the "N more" summaries (highest wins, as on its row). */
  function toneOf(chat: Company) {
    const isActive = chat.id === activeCompanyId;
    return rowState({ status: sessionStatusByCompany?.[chat.id], unread: shownUnread(chat), isActive: isActive && unreadHoldId !== chat.id }).tone;
  }
  function newsOf(list: Company[]) {
    const tones = list.map(toneOf);
    return {
      needs: tones.filter((t) => t === "needs").length,
      working: tones.filter((t) => t === "working" || t === "starting").length,
      unread: tones.filter((t) => t === "unread").length,
    };
  }
  function newsWords(n: { needs: number; working: number; unread: number }) {
    return [n.needs ? `${n.needs} need${n.needs === 1 ? "s" : ""} you` : "", n.working ? `${n.working} working` : "", n.unread ? `${n.unread} unread` : ""].filter(Boolean);
  }
  /** Counted state tokens in their meaning colours: amber needs you · orb working · blue unread. */
  function renderSum(n: { needs: number; working: number; unread: number }) {
    if (!n.needs && !n.working && !n.unread) return null;
    return (
      <span className="sbSum" aria-hidden="true">
        {n.needs > 0 && <span className="sbSum__item is-needs"><span className="sbDot is-needs is-small" />{n.needs}</span>}
        {n.working > 0 && <span className="sbSum__item is-working"><WorkingOrb />{n.working}</span>}
        {n.unread > 0 && <span className="sbSum__item is-unread"><span className="sbDot is-unread is-small" />{n.unread}</span>}
      </span>
    );
  }

  /** A pinned chat as a face tile in the phone grid: tap opens it, a hold opens its sheet (Unpin first). */
  function renderTile(chat: Company, cols: number) {
    const isActive = chat.id === activeCompanyId;
    const status = sessionStatusByCompany?.[chat.id];
    const unread = shownUnread(chat);
    const state = rowState({ status, unread, isActive: isActive && unreadHoldId !== chat.id });
    const working = state.tone === "working" || state.tone === "starting";
    const menuOpen = menu?.id === chat.id && menu.place === "tile";
    const label = shortName(chat.name);
    // Working / starting / needs you: the name keeps one line and the state takes the second
    // (both lines are always reserved, so a tile never changes height).
    const stateLine = state.tone === "working" ? <span className="sbTile__state is-working"><WorkingWord /></span>
      : state.tone === "starting" ? <span className="sbTile__state is-working">Starting…</span>
      : state.tone === "needs" ? <span className="sbTile__state is-needs">Needs you</span> : null;
    // A list of buttons (break-it r2 minor): the list item is the cell, the tile inside it stays a
    // real button (a role="listitem" on the button hid it from screen readers as a control).
    return (
      <div key={`tile-${chat.id}`} role="listitem" className="sbPinGrid__cell">
      <button type="button"
        className={`sbTile ${isActive ? "is-active" : ""} ${state.tone !== "none" ? `is-${state.tone}` : ""} ${working ? "is-live" : ""} ${menuOpen ? "is-menu" : ""} ${pressingId === chat.id ? "is-pressing" : ""} ${stateLine ? "has-state" : ""}`}
        data-chat-id={chat.id} data-no-swipe="true"
        onClick={() => {
          if (pressRef.current?.fired) { pressRef.current = null; return; }
          onSelect(chat.id);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          if (lastPointerRef.current === "touch") return;
          openChatMenu(chat, e.currentTarget, { x: e.clientX, y: e.clientY }, "tile");
        }}
        onPointerDown={(e) => {
          lastPointerRef.current = e.pointerType;
          if (pressRef.current?.fired) pressRef.current = null;
          if (e.pointerType !== "touch") return;
          const tile = e.currentTarget;
          const press = { x: e.clientX, y: e.clientY, fired: false, id: chat.id, pid: e.pointerId, timer: 0 };
          press.timer = window.setTimeout(() => {
            if (pressRef.current !== press) return;
            press.fired = true;
            swallowLiftClick(press.pid);
            setPressingId(null);
            vibrate(12);
            openChatMenu(chat, tile, undefined, "tile");
          }, LONG_PRESS_MS);
          pressRef.current = press;
          setPressingId(chat.id);
        }}
        onPointerMove={(e) => {
          const press = pressRef.current;
          if (press && !press.fired && Math.hypot(e.clientX - press.x, e.clientY - press.y) > PRESS_SLOP) cancelPress();
        }}
        onPointerUp={() => { if (pressRef.current && !pressRef.current.fired) cancelPress(); }}
        onPointerCancel={() => { if (pressRef.current && !pressRef.current.fired) cancelPress(); }}
        onPointerLeave={() => { if (pressRef.current && !pressRef.current.fired) cancelPress(); }}
        onKeyDown={(e) => tileKeys(e, cols, () => openChatMenu(chat, e.currentTarget, undefined, "tile"))}
        aria-label={`${chat.name}, pinned${state.a11y ? `, ${state.a11y}` : ""}`}
        aria-current={isActive ? "true" : undefined}
        title={chat.name}>
        <span className="sbTile__face">
          <Face id={chat.id} working={working} size={44} />
          {working && <span className="sbTile__orb" aria-hidden="true"><WorkingOrb /></span>}
          {!working && state.tone === "needs" && <span className="sbDot is-needs sbTile__badge" aria-hidden="true" />}
          {!working && state.tone === "unread" && <span className="sbDot is-unread sbTile__badge" aria-hidden="true" />}
        </span>
        <span className="sbTile__text">
          <span className="sbTile__name">{label}</span>
          {stateLine}
        </span>
      </button>
      </div>
    );
  }

  /** Arrow keys in the phone grid: ← / → by one tile, ↑ / ↓ by a row of tiles (a short last row: its
   *  last tile), and out of the grid past its first / last row; Shift+F10 / ContextMenu = the menu. */
  function tileKeys(e: React.KeyboardEvent<HTMLElement>, cols: number, menuFor?: () => void) {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const tiles = visibleOnly(Array.from(e.currentTarget.closest(".sbPinGrid")?.querySelectorAll<HTMLElement>(".sbTile") ?? []));
      const at = tiles.indexOf(e.currentTarget);
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") { tiles[at + (e.key === "ArrowRight" ? 1 : -1)]?.focus(); return; }
      const down = e.key === "ArrowDown";
      const row = Math.floor(at / cols), lastRow = Math.floor((tiles.length - 1) / cols);
      if (down ? row < lastRow : row > 0) { tiles[Math.min(at + (down ? cols : -cols), tiles.length - 1)]?.focus(); return; }
      focusSibling(down ? tiles[tiles.length - 1] : tiles[0], down ? 1 : -1);
    } else if (menuFor && ((e.key === "F10" && e.shiftKey) || e.key === "ContextMenu")) {
      e.preventDefault(); menuFor();
    }
  }

  function renderPinned() {
    if (pinned.length === 0) return null;
    const all = newsOf(pinned);
    // Who must stay in view when folded: the chat being renamed, the one just pinned, the open chat.
    const keep = [renamingId, lastPinnedId, activeCompanyId];
    const rows = foldPinned(pinned, budget.lines, keep, pinnedExpanded);
    const cells = foldPinned(pinned, budget.gridRows * budget.gridCols, keep, pinnedExpanded);
    const overflowRows = pinned.length > budget.lines, overflowCells = pinned.length > budget.gridRows * budget.gridCols;
    const headLess = pinnedExpanded && !pinnedFolded && (overflowRows || overflowCells);
    const sumAll = renderSum(all);
    const allWords = newsWords(all);
    return (
      <section className={`sbSection sbPinned ${pinnedFolded ? "is-folded" : ""} ${pinnedExpanded ? "is-expanded" : ""} ${pinned.some((c) => c.id === renamingId) ? "is-renaming" : ""}`}
        aria-label="Pinned">
        <div className={`sbPinned__head ${pinHeadStuck ? "is-stuck" : ""} ${headLess ? "has-less" : ""} ${headLess && !overflowCells ? "is-rows-only" : ""} ${headLess && !overflowRows ? "is-cells-only" : ""}`}>
          <button type="button" className="sbPinned__toggle" onClick={togglePinnedFold}
            aria-expanded={!pinnedFolded} aria-controls="sb-pinned-body"
            aria-label={`Pinned, ${pinned.length}${allWords.length ? `, ${allWords.join(", ")}` : ""}. ${pinnedFolded ? "Show" : "Hide"} pinned chats`}
            title={pinnedFolded ? "Show pinned chats" : "Hide pinned chats"}>
            <span className="sbSection__label sbPinned__label">Pinned</span>
            <span className="sbPinned__count">{pinned.length}</span>
            {pinnedFolded && sumAll}
            <span className="sbPinned__chevron" aria-hidden="true">{I.chevron}</span>
          </button>
          {headLess && (
            <button type="button" className="sbPinned__less" onClick={(e) => setPinsExpanded(false, e.currentTarget, null, e.detail === 0)}
              aria-controls="sb-pinned-body" title="Back to the first pinned chats">Show less</button>
          )}
        </div>
        {!pinnedFolded && (
          <div id="sb-pinned-body" className="sbPinned__body">
            <div role="list" aria-label="Pinned chats" data-no-swipe="true" className="sbPinGrid" style={{ "--sb-pin-cols": budget.gridCols } as React.CSSProperties}>
              {cells.shown.map((chat) => renderTile(chat, budget.gridCols))}
              {overflowCells && renderMoreTile(cells.hidden, pinnedExpanded)}
            </div>
            <div className="sbPinned__rows sbSection__rows">
              {rows.shown.map((chat) => renderRow(chat, "pinned", true))}
              {overflowRows && renderMoreLine(rows.hidden, pinnedExpanded)}
            </div>
          </div>
        )}
      </section>
    );
  }

  /** "Show 12 more" (rows): one line the height of a row, carrying what the hidden pins hold. */
  function renderMoreLine(hidden: Company[], expanded: boolean) {
    const news = newsOf(hidden);
    const words = newsWords(news);
    const hasActive = hidden.some((c) => c.id === activeCompanyId);
    const sum = renderSum(news);
    return (
      <button key="pin-more" type="button" tabIndex={0}
        className={`sbPinMore ${expanded ? "is-expanded" : ""} ${hasActive && !expanded ? "is-active" : ""}`}
        aria-expanded={expanded} aria-controls="sb-pinned-body"
        aria-label={expanded ? "Show fewer pinned chats" : `Show ${hidden.length} more pinned chats${words.length ? `: ${words.join(", ")}` : ""}${hasActive ? ", including the open chat" : ""}`}
        onClick={(e) => setPinsExpanded(!expanded, e.currentTarget, hidden[0]?.id, e.detail === 0)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); focusSibling(e.currentTarget, e.key === "ArrowDown" ? 1 : -1); }
        }}>
        <span className="sbPinMore__icon" aria-hidden="true">{expanded ? I.chevronUp : I.chevron}</span>
        <span className="sbPinMore__text">{expanded ? "Show less" : `Show ${hidden.length} more`}</span>
        {!expanded && (sum ?? (
          <span className="sbPinMore__faces" aria-hidden="true">
            {hidden.slice(0, 3).map((c) => <ChatAvatar key={c.id} id={c.id} working={false} size={18} />)}
          </span>
        ))}
      </button>
    );
  }

  /** The phone grid's last cell: a folder of the hidden pins' faces, "8 more" and what they hold. */
  function renderMoreTile(hidden: Company[], expanded: boolean) {
    const news = newsOf(hidden);
    const words = newsWords(news);
    const hasActive = hidden.some((c) => c.id === activeCompanyId);
    // The strongest hidden state, in words and its colour (a reply or a waiting helper never hides).
    const top = news.needs ? { cls: "is-needs", text: "Needs you" } : news.working ? { cls: "is-working", text: `${news.working} working` }
      : news.unread ? { cls: "is-unread", text: `${news.unread} unread` } : null;
    const faces = [...hidden.filter((c) => toneOf(c) !== "none"), ...hidden.filter((c) => toneOf(c) === "none")].slice(0, 4);
    return (
      <div key="tile-more" role="listitem" className="sbPinGrid__cell">
      <button type="button" data-no-swipe="true"
        className={`sbTile sbTile--more ${expanded ? "is-expanded" : ""} ${hasActive && !expanded ? "is-active" : ""} ${top && !expanded ? top.cls : ""}`}
        aria-expanded={expanded} aria-controls="sb-pinned-body"
        aria-label={expanded ? "Show fewer pinned chats" : `Show ${hidden.length} more pinned chats${words.length ? `: ${words.join(", ")}` : ""}${hasActive ? ", including the open chat" : ""}`}
        onClick={(e) => setPinsExpanded(!expanded, e.currentTarget, hidden[0]?.id, e.detail === 0)}
        onKeyDown={(e) => tileKeys(e, budget.gridCols)}>
        <span className="sbTile__face">
          {expanded ? <span className="sbTile__folder is-less" aria-hidden="true">{I.chevronUp}</span> : (
            <span className="sbTile__folder" aria-hidden="true">
              {faces.map((c) => <ChatAvatar key={c.id} id={c.id} working={false} size={18} />)}
            </span>
          )}
          {/* Corner marks for what hides inside: amber (needs you) or blue (unread) top right, the orb if one works. */}
          {!expanded && news.needs > 0 && <span className="sbDot is-needs sbTile__badge" aria-hidden="true" />}
          {!expanded && !news.needs && news.unread > 0 && <span className="sbDot is-unread sbTile__badge" aria-hidden="true" />}
          {!expanded && news.working > 0 && <span className="sbTile__orb" aria-hidden="true"><WorkingOrb /></span>}
        </span>
        <span className="sbTile__text">
          <span className="sbTile__name">{expanded ? "Show less" : `${hidden.length} more`}</span>
          {!expanded && top && <span className={`sbTile__state ${top.cls}`}>{top.text}</span>}
        </span>
      </button>
      </div>
    );
  }

  /** Where the open popover goes, from its MEASURED size (the first, hidden pass measures it). */
  function popoverStyle(isFilter: boolean): { style: React.CSSProperties; side: PopoverPlacement["side"] | "measuring" } {
    const width = isFilter ? 232 : 256;
    if (!menuMeasure || menuMeasure.menu !== menu) return { style: { left: 0, top: 0, width, visibility: "hidden" }, side: "measuring" };
    const point = menu?.x != null && menu?.y != null ? { left: menu.x, right: menu.x, top: menu.y, bottom: menu.y } : null;
    const a = point ?? menu?.anchor ?? { left: 16, right: 16, top: 16, bottom: 16 };
    const place = placePopover(a, { width, height: menuMeasure.h }, { width: window.innerWidth, height: window.innerHeight },
      { align: point ? "start" : isFilter ? "center" : "end", gap: point ? 2 : 6 });
    return { style: { left: place.left, top: place.top, width, ...(place.maxHeight ? { maxHeight: place.maxHeight } : {}) }, side: place.side };
  }

  function renderMenu() {
    if (!menu) return null;
    const isFilter = menu.id === FILTER_MENU;
    const chat = isFilter ? null : companies.find((c) => c.id === menu.id);
    if (!isFilter && !chat) return null;
    const popover = popoverMode();
    const placed = popover && typeof window !== "undefined" ? popoverStyle(isFilter) : null;
    if (isFilter) return renderFilterMenu(placed, popover);
    if (!chat) return null;
    const kind = chatKind(chat);
    const lastAt = lastActivityMs(chat, chatActivity[chat.id], unreadByCompany[chat.id]);
    const host = siteHost(chat);
    // Two facts lines (fixer r1 #13): what and where, then when — a break never falls inside the date.
    const subtitle = [KIND_LABEL[kind], host].filter(Boolean).join(" · ");
    const lastActive = lastAt ? `Last active ${formatExactTime(lastAt, clock, "auto")}` : "";
    const archived = isArchived(chat);
    const pinnedNow = isPinned(chat);
    const unreadNow = shownUnread(chat);
    const close = () => setMenu(null);
    const titleId = "sb-menu-title";
    const compactable = sessionStatusByCompany?.[chat.id]?.compactable === true && compactingByCompany[chat.id]?.state !== "compacting";
    return createPortal(
      <div ref={dialogRootRef} tabIndex={-1} className={`sbMenu ${popover ? "is-popover" : "is-sheet"} ${placed ? `is-${placed.side}` : ""}`}
        onClick={(e) => { e.stopPropagation(); close(); }} onContextMenu={(e) => { e.preventDefault(); close(); }}
        role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div ref={menuPanelRef} className="sbMenu__panel" style={placed?.style} onClick={(e) => e.stopPropagation()}>
          <div className="sbMenu__grabber" aria-hidden="true" />
          {/* The head says which chat this is: its face, its name, what and where it is. */}
          <div className="sbMenu__head">
            <span className="sbMenu__face"><ChatAvatar id={chat.id} working={false} size="var(--sb-menu-face, 28px)" /></span>
            <span className="sbMenu__headText">
              <span className="sbMenu__title" id={titleId}>{chat.name}</span>
              <span className="sbMenu__subtitle">
                <span className="sbMenu__fact">{subtitle}</span>
                {lastActive && <span className="sbMenu__fact">{lastActive}</span>}
              </span>
            </span>
          </div>
          {/* Four groups by how often they are used: follow-up · edit · open · destructive (red, last). */}
          <div className="sbMenu__items">
            <div className="sbMenu__group" role="group" aria-label="Follow-up">
              {!archived && (
                <button type="button" className="sbMenu__item" data-dialog-initial onClick={() => { close(); togglePin(chat, menu.place === "tile" ? "pinned-card" : "menu", menu.place === "attention" || menu.place === "tile" || menu.place === "pinned"); }}>
                  {pinnedNow ? I.unpin : I.pin}<span>{pinnedNow ? "Unpin" : "Pin to top"}</span>
                </button>
              )}
              <button type="button" className="sbMenu__item" onClick={() => { close(); toggleUnread(chat, menu.place === "tile" ? "pinned-card" : "menu"); }}>
                {unreadNow ? I.read : I.dot}
                <span>{unreadNow ? "Mark as read" : "Mark as unread"}</span>
              </button>
              <button type="button" className="sbMenu__item" {...(archived ? { "data-dialog-initial": true } : {})}
                onClick={() => { close(); void setArchived(chat, !archived, "menu"); }}>
                {I.archive}<span>{archived ? "Unarchive" : "Archive (hide)"}</span>
              </button>
            </div>
            <div className="sbMenu__group" role="group" aria-label="Edit">
              <button type="button" className="sbMenu__item" onClick={() => {
                suppressDialogRestoreRef.current = true;
                close();
                window.setTimeout(() => startRename(chat), 60);
              }}>
                {I.edit}<span>Rename</span>
              </button>
              <button type="button" className="sbMenu__item" onClick={() => { suppressDialogRestoreRef.current = true; close(); onSettings(chat.id); }}>
                {I.settings}<span>Settings</span>
              </button>
              {chat.kind === "sample" && (
                <button type="button" className="sbMenu__item" onClick={() => { close(); onConvertToClient(chat.id); }}>
                  {I.convert}<span>Convert to client</span>
                </button>
              )}
            </div>
            <div className="sbMenu__group" role="group" aria-label="Open">
              <button type="button" className="sbMenu__item" onClick={() => { close(); onVisitWebsite(chat.id); }}>
                {I.site2}<span>Visit website</span><i className="sbMenu__trail" aria-hidden="true">{I.out}</i>
              </button>
              <button type="button" className="sbMenu__item" onClick={() => { close(); onCopyCurl(chat.id); }}>
                {I.copy}<span>Copy curl</span>
              </button>
              {compactable && (
                <button type="button" className="sbMenu__item" onClick={() => { close(); void compactCompany(chat.id); }}>
                  {I.compact}<span>Compact context</span>
                </button>
              )}
            </div>
            <div className="sbMenu__group" role="group" aria-label="Clear or remove">
              <button type="button" className="sbMenu__item" onClick={() => { close(); onClearHistory(chat.id); }}>
                {I.clear}<span>Clear chat</span>
              </button>
              <button type="button" className="sbMenu__item sbMenu__item--danger" onClick={() => {
                suppressDialogRestoreRef.current = true;
                close();
                setDeleteTyped("");
                window.setTimeout(() => setDeletingId(chat.id), 60);
              }}>
                {I.trash}<span>{kind === "client" ? "Remove client" : "Delete"}</span>
              </button>
            </div>
          </div>
          <button type="button" className="sbMenu__cancel" onClick={close}>Cancel</button>
        </div>
      </div>,
      document.body,
    );
  }

  function renderFilterMenu(placed: ReturnType<typeof popoverStyle> | null, popover: boolean) {
    const close = () => setMenu(null);
    const counts = filterCounts(inputs, clock);
    const icon: Record<SidebarFilter, React.ReactNode> = {
      all: I.list,
      unread: I.dot,
      // Working wears what a working row wears: the thinking orb (colour = meaning).
      working: <span className="sbFilterMenu__orb"><WorkingOrb /></span>,
      pinned: I.pin,
    };
    return createPortal(
      <div ref={dialogRootRef} tabIndex={-1} className={`sbMenu sbFilterMenu ${popover ? "is-popover" : "is-sheet"} ${placed ? `is-${placed.side}` : ""}`}
        onClick={(e) => { e.stopPropagation(); close(); }} onContextMenu={(e) => { e.preventDefault(); close(); }}
        role="dialog" aria-modal="true" aria-labelledby="sb-filter-title">
        <div ref={menuPanelRef} className="sbMenu__panel" style={placed?.style} onClick={(e) => e.stopPropagation()}>
          <div className="sbMenu__grabber" aria-hidden="true" />
          <div className="sbMenu__title sbFilterMenu__title" id="sb-filter-title">Show</div>
          <div className="sbMenu__items" role="radiogroup" aria-labelledby="sb-filter-title">
            <div className="sbMenu__group">
              {FILTER_ORDER.map((key) => (
                <button key={key} type="button" role="radio" aria-checked={filter === key}
                  className={`sbMenu__item sbFilterMenu__item ${filter === key ? "is-on" : ""}`}
                  {...(filter === key ? { "data-dialog-initial": true } : {})}
                  title={filter === key && key !== "all" ? "Show all chats again" : undefined}
                  onClick={() => { close(); chooseFilter(key); }}>
                  <span className="sbFilterMenu__label">{icon[key]}{FILTER_LABEL[key]}</span>
                  <span className="sbFilterMenu__count">{counts[key]}</span>
                  <span className="sbFilterMenu__check" aria-hidden="true">
                    {filter === key ? I.check : null}
                    {filter === key && key !== "all" ? <span className="sbFilterMenu__undo">{I.x}</span> : null}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="sbMenu__cancel" onClick={close}>Cancel</button>
        </div>
      </div>,
      document.body,
    );
  }

  function renderDelete() {
    if (!deletingId) return null;
    const target = companies.find((c) => c.id === deletingId);
    if (!target) return null;
    const copy = deleteCopy(target);
    const ready = !copy.requireName || nameConfirmed(target, deleteTyped);
    const closeIt = () => setDeletingId(null);
    return (
      <div ref={dialogRootRef} tabIndex={-1} className="sbConfirm" onClick={closeIt}
        role="dialog" aria-modal="true" aria-labelledby="sb-delete-title" aria-describedby="sb-delete-body">
        <div className="sbConfirm__panel" onClick={(e) => e.stopPropagation()}>
          <h3 className="sbConfirm__title" id="sb-delete-title">{copy.title}</h3>
          <p className="sbConfirm__body" id="sb-delete-body">{copy.body}</p>
          {copy.requireName && (
            <label className="sbConfirm__field">
              <span>Type <strong>{target.name}</strong> to confirm</span>
              <input type="text" value={deleteTyped} onChange={(e) => setDeleteTyped(e.currentTarget.value)}
                autoComplete="off" autoCapitalize="off" spellCheck={false} aria-label={`Type ${target.name} to confirm`}
                {...(!copy.archiveFirst ? { "data-dialog-initial": true } : {})} />
            </label>
          )}
          <div className="sbConfirm__buttons">
            <button type="button" className="sbConfirm__btn" onClick={closeIt} {...(!copy.requireName ? { "data-dialog-initial": true } : {})}>Cancel</button>
            <button type="button" className="sbConfirm__btn is-danger" disabled={!ready}
              onClick={() => { if (!ready) return; onDelete(target.id); closeIt(); }}>{copy.confirm}</button>
            {copy.archiveFirst && (
              <button type="button" className="sbConfirm__btn is-primary" data-dialog-initial
                onClick={() => { closeIt(); void setArchived(target, true, "delete-sheet"); }}>Archive instead</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderEmpty() {
    if (showArchived) {
      return <div className="sbEmpty">
        <div className="sbEmpty__title">{searching ? "Nothing archived matches that" : tab === "samples" ? "No archived samples" : "No archived clients"}</div>
        <div className="sbEmpty__hint">{searching ? "Try another name." : "Archive a chat from its menu to tidy the list. It keeps working and can come back any time."}</div>
      </div>;
    }
    if (searching) {
      return <div className="sbEmpty">
        <div className="sbEmpty__title">Nothing matches “{searchQuery.trim()}”</div>
        <div className="sbEmpty__hint">Try another name{archivedTotal ? ", or look in Archived" : ""}.</div>
      </div>;
    }
    if (tabCount > 0) {
      return <div className="sbEmpty">
        <div className="sbEmpty__title">Everything here is archived</div>
        <div className="sbEmpty__hint">Open Archived below to see them.</div>
      </div>;
    }
    return <div className="sbEmpty">
      <div className="sbEmpty__title">{tab === "samples" ? "No samples yet" : "No clients yet"}</div>
      <div className="sbEmpty__hint">{tab === "samples" ? "Tap + to make a demo for a lead." : "Tap + to add your first client."}</div>
    </div>;
  }

  const listEmpty = (filter === "all" || searching) && attention.length === 0 && pinned.length === 0 && sections.length === 0;
  const undoText = undo ? (undo.kind === "archive" ? `Archived “${undo.name}”` : undo.kind === "pin" ? `Pinned “${undo.name}”` : `Unpinned “${undo.name}”`) : "";
  const shownArchived = showArchived ? visibleChats(inputs).length : 0;

  return (
    <>
      {open && <div className="sbScrim" onClick={onCloseSidebar} aria-hidden="true" />}

      <aside ref={sbRef} className="sb" role="complementary" aria-label="Chats" aria-hidden={!open} inert={!open}>
        {resizeHandle()}
        <header className={`sb__header ${showArchived ? "is-archived" : ""}`}>
          {!showArchived && (
            <span className="sb__lead">
              {activeCompanyId && (
                <button type="button" className="sbIconBtn sb__phoneClose" onClick={onCloseSidebar} aria-label="Close chats" title="Back to the chat">{I.x}</button>
              )}
            </span>
          )}
          {showArchived ? (
            <button type="button" className="sb__back" onClick={() => viewArchived(false)} aria-label="Back to chats">
              {I.back}
              <span className="sb__title">Archived</span>
              <span className="sb__titleCount">{archivedTotal}</span>
            </button>
          ) : (
            <span className="sb__title">Chats</span>
          )}
          <div className="sb__headerActions">
            {!showArchived && (
              <button type="button" className={`sbIconBtn sb__filter ${filter !== "all" ? `is-on is-${filter}` : ""} ${menu?.id === FILTER_MENU ? "is-open" : ""}`}
                onClick={(e) => {
                  if (menu?.id === FILTER_MENU) { setMenu(null); return; }
                  dialogReturnFocusRef.current = e.currentTarget;
                  setMenu({ id: FILTER_MENU, anchor: rectOf(e.currentTarget) });
                }}
                aria-label={`Filter chats: ${FILTER_LABEL[filter]}`} aria-haspopup="dialog" aria-expanded={menu?.id === FILTER_MENU}
                title={filter === "all" ? "Filter: All chats · Unread · Working · Pinned" : `Showing ${FILTER_LABEL[filter].toLowerCase()} only`}>
                {I.filter}
                {filter !== "all" && <span className="sb__filterDot" aria-hidden="true" />}
              </button>
            )}
            {!showArchived && (
              <button type="button" className="sbIconBtn sb__new"
                onClick={tab === "samples" ? onAddSample : onAddCompany}
                aria-label={tab === "samples" ? "New sample" : "Add client"}
                title={tab === "samples" ? "New sample — a demo for a lead" : "Add client"}>
                {I.plus}
              </button>
            )}
            <button type="button" className="sbIconBtn sb__close" onClick={onCloseSidebar} aria-label="Hide chats" title="Hide chats">{I.panel}</button>
          </div>
        </header>

        <div className="sb__top">
          <div className="sb__search">
            <span className="sb__searchIcon">{I.search}</span>
            <input
              ref={searchRef}
              type="search"
              className="sb__searchInput"
              placeholder={showArchived ? "Search archived" : tab === "samples" ? "Search samples" : "Search clients"}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape" && searchQuery) { e.preventDefault(); e.stopPropagation(); setSearchQuery(""); }
                else if (e.key === "ArrowDown") { e.preventDefault(); visibleOnly(Array.from(listRef.current?.querySelectorAll<HTMLElement>(ROVING) ?? []))[0]?.focus(); }
              }}
              aria-label="Search chats"
              enterKeyHint="search"
              autoComplete="off"
            />
            {searchQuery && (
              <button type="button" className="sb__searchClear" onClick={() => { setSearchQuery(""); searchRef.current?.focus(); }} aria-label="Clear search">{I.x}</button>
            )}
          </div>
          <div className="sbSeg" role="tablist" aria-label="Show clients or samples">
            {(["clients", "samples"] as const).map((key) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key}
                className={`sbSeg__opt ${tab === key ? "is-on" : ""}`} onClick={() => switchTab(key)}>
                {key === "clients" ? "Clients" : "Samples"}
                {key === "clients" && tab !== "clients" && clientsNeedAttention && <span className="sbDot is-needs is-small" role="img" aria-label="Revisions waiting" />}
              </button>
            ))}
          </div>
          {showArchived && shownArchived > 0 && (
            <p className="sb__note">Archived — hidden from the list for everyone. Revisions still show in Attention.</p>
          )}
        </div>

        <div className="sb__list" ref={listRef}>
          {showArchived ? (
            sections.length === 0 ? renderEmpty() : (
              <div className="sbSection__rows">{sections[0].chats.map((chat) => renderRow(chat, "list"))}</div>
            )
          ) : listEmpty ? renderEmpty() : (
            <>
              {attention.length > 0 && (
                <section className="sbSection sbAttention" aria-label="Attention">
                  <h3 className="sbSection__label is-attention">Attention</h3>
                  <div className="sbSection__rows">{attention.map((chat) => renderRow(chat, "attention"))}</div>
                </section>
              )}
              {renderPinned()}
              {sections.map((section) => (
                <section key={section.key} className={`sbSection ${section.key === "filter" ? "sbFiltered" : ""}`} aria-label={section.label || "Results"}>
                  {section.key === "filter" ? (
                    <div className="sbFiltered__head">
                      <h3 className="sbSection__label">{section.label}<span className="sbFiltered__count">{section.chats.length}</span></h3>
                      <button type="button" className="sbFiltered__clear" onClick={() => setFilter("all")}>Show all</button>
                    </div>
                  ) : section.label && <h3 className="sbSection__label">{section.label}</h3>}
                  <div className="sbSection__rows">{section.chats.map((chat) => renderRow(chat, section.key === "filter" && filter === "pinned" ? "pinned" : "list"))}</div>
                </section>
              ))}
              {filter !== "all" && !searching && sections.length === 0 && (
                <div className="sbEmpty sbEmpty--filter">
                  <div className="sbEmpty__title">{filter === "unread" ? "No unread chats" : filter === "working" ? "Nothing is working" : "Nothing pinned"}</div>
                  <div className="sbEmpty__hint">{filter === "unread" ? "Every reply has been opened." : filter === "working" ? "No agent is running right now. Chats show here while one works." : "Hold a chat (or use its menu) to pin it for the team."}</div>
                  <button type="button" className="sbFiltered__clear" onClick={() => setFilter("all")}>Show all chats</button>
                </div>
              )}
            </>
          )}
        </div>

        {undo && (
          <div className="sbUndo" role="status">
            <span className="sbUndo__text">{undoText}</span>
            <button type="button" className="sbUndo__btn" onClick={(e) => {
              const current = undo;
              if (undoTimerRef.current) { window.clearTimeout(undoTimerRef.current); undoTimerRef.current = null; }
              // The toast unmounts: focus goes to the restored row (or search), never <body>.
              if (document.activeElement === e.currentTarget) {
                focusFollowRef.current = { el: e.currentTarget, id: current.id, after: [], before: [], until: Date.now() + 3_000 };
              }
              setUndo(null);
              if (typeof setChatFlag !== "function" || !companies.some((c) => c.id === current.id)) return;
              if (current.kind === "archive") {
                // Undo puts it back exactly: a chat that was pinned goes back to its old place in Pinned.
                void setChatFlag(current.id, current.pinned ? "pinned" : "archived", current.pinned ? true : false, "undo", current.pinnedAt);
              } else {
                void setChatFlag(current.id, "pinned", current.pinned, "undo", current.pinned ? current.pinnedAt : null);
              }
            }}>Undo</button>
          </div>
        )}

        <footer className={`sb__footer ${showArchived ? "is-archived" : ""}`}>
          {!showArchived && (
            <button type="button" className="sb__footerBtn" onClick={() => viewArchived(true)}
              aria-label={`Archived ${tab === "samples" ? "samples" : "clients"}, ${archivedTotal}`}>
              {I.archive}
              <span>Archived</span>
              {archivedTotal > 0 && <span className="sb__footerCount">{archivedTotal}</span>}
            </button>
          )}
        </footer>
      </aside>

      {renderMenu()}
      {renderDelete()}
    </>
  );
}
